/**
 * Supabaseサービス
 * 本番環境用のデータベース接続とCRUD操作を提供
 */

import { SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient, isSupabaseAvailable, debugSupabaseConnection } from '../utils/supabase';
import { DemoDataFallbackService } from './demoDataFallback';
import {
  User,
  Role,
  Client,
  LandTransporter,
  Case,
  VehicleModel,
  VehicleModelDataSource,
  VehicleType,
  Manufacturer,
  BusinessPartner,
  BusinessPartnerLocation,
  BusinessPartnerRole,
  BusinessPartnerDataSource,
  BusinessPartnerType,
  BusinessPartnerCategory,

  // マルチテナント対応型定義
  Company,
  CompanyCase,
  CompanyPartnerSelection,
  CompanyPartnerSettings,
  CompanyVehicleModelSettings,
} from '../types';
import { AuthService, MasterDataService, DataServiceError, ErrorCodes } from './dataService';
import { getSupabaseConfig, isDemoMode, debugLog } from '../utils/environment';
import { convertCompanyCasesToCases } from '../utils/caseConverters';
import logger from '../utils/logger';

// 認証状態保存用のlocalStorageキー
const AUTH_STORAGE_KEY = 'fleetpilot_mock_current_user';

// Supabase認証サービス
export class SupabaseAuthService implements AuthService {
  private supabase: SupabaseClient | null = null;
  private authStateChangeCallback?: (user: User | null) => void;
  private isDemo: boolean;

  constructor(demoMode?: boolean) {
    this.isDemo = demoMode ?? isDemoMode();

    // 全モードでSupabaseクライアントを初期化（ログイン機能用）
    this.initializeSupabase();

    // リスナーは本番モードのみ設定
    if (!this.isDemo) {
      this.setupAuthListener();
    } else {
      debugLog('SupabaseAuthService: デモモードで初期化（Supabaseクライアント利用可能）');
    }
  }

  private initializeSupabase() {
    try {
      // シングルトンSupabaseクライアントを使用
      this.supabase = getSupabaseClient();

      if (!this.supabase) {
        logger.warn('Supabaseの設定が見つかりません。環境変数を確認してください。');
        return;
      }

      debugLog('SupabaseAuthService: シングルトンクライアント取得完了');
    } catch (error) {
      logger.error('Supabase initialization failed', { error });
    }
  }

  async loginWithRole(role: Role): Promise<User> {
    // デモモード時は常にデモ用ログインを使用
    if (this.isDemo || !this.supabase) {
      console.log('🎭 SupabaseAuthService: デモモードまたはSupabase未設定 - デモログイン実行');
      return this.loginWithRoleDemo(role);
    }

    throw new DataServiceError(
      'Supabase認証機能は実装中です。現在はデモ用ログインをご利用ください。',
      ErrorCodes.AUTHENTICATION_FAILED
    );
  }

  private async loginWithRoleDemo(role: Role): Promise<User> {
    // FL-104: Supabaseベースのデモ用ログイン機能（リモートデータ使用）
    const demoUsers = {
      [Role.SUPER_ADMIN]: {
        name: 'ラクハイ管理者デモ',
        company: '',
        companyId: undefined,
      },
      [Role.ADMIN]: {
        name: 'サンプル管理者',
        company: '株式会社サンプル陸送',
        companyId: 'e32a0499-6456-44ac-9b16-b0001098c259', // デモ会社ID統一
      },
      [Role.DISPATCHER]: {
        name: 'サンプル配車担当',
        company: '株式会社サンプル陸送',
        companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
      },
      [Role.DRIVER]: {
        name: 'サンプル陸送員A',
        company: '株式会社サンプル陸送',
        companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
        landTransporterId: 'demo-driver-1',
      },
      [Role.LAND_TRANSPORTER]: {
        name: 'サンプル陸送員B',
        company: '株式会社サンプル陸送',
        companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
        landTransporterId: '0b00ad35-df7c-4347-a02e-0b7b378d83e4',
      },
      [Role.MANAGER]: {
        name: 'サンプル管理者（マネージャー）',
        company: '株式会社サンプル陸送',
        companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
      },
      [Role.READ_ONLY]: {
        name: '閲覧専用デモ',
        company: '株式会社サンプル陸送',
        companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
      },
    };

    const userData = demoUsers[role];
    const user: User = {
      id: crypto.randomUUID(),
      name: userData.name,
      role,
      company: userData.company,
      companyId: userData.companyId,
      landTransporterId: 'landTransporterId' in userData ? userData.landTransporterId : undefined,
    };

    // デモモード時もSupabaseのデモデータを使用するため、localStorage認証は使用しない
    if (this.isDemo) {
      console.log('🎭 SupabaseAuthService: デモモード認証完了', {
        role,
        companyId: user.companyId,
        name: user.name,
      });
    } else {
      // 非デモモード時のみlocalStorage保存（従来の動作維持）
      try {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
      } catch (error) {
        console.warn('Failed to save auth state to localStorage:', error);
      }
    }

    return user;
  }

  async loginWithEmail(email: string, password: string): Promise<User> {
    // 1. Supabase認証を最優先で試行（実際のアカウント用）
    if (this.supabase) {
      if (import.meta.env.DEV) {
        console.log('🔍 loginWithEmail: Supabase認証を試行（実際のアカウント）');
      }

      try {
        // Supabase認証実行
        const { data, error } = await this.supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (!error && data.user && data.session) {
          if (import.meta.env.DEV) {
            console.log('✅ Supabase認証成功:', { email, userId: data.user.id });
            console.log('🔐 Supabaseセッション情報:', {
              hasAccessToken: !!data.session.access_token,
              accessTokenLength: data.session.access_token?.length || 0,
              tokenType: data.session.token_type,
            });
          }

          // 🔧 修正: Supabase セッションが正しく localStorage に保存されているか確認
          try {
            const { data: currentSession } = await this.supabase.auth.getSession();
            if (!currentSession.session) {
              console.warn(
                '⚠️ loginWithEmail: Supabase セッションが取得できません。手動復元を試行...'
              );
              // セッションが見つからない場合は明示的に設定
              // TODO: Supabase v2.54以降の手動セッション復元メカニズム確認
            } else if (import.meta.env.DEV) {
              console.log('✅ loginWithEmail: Supabase セッションが正しく復元されました');
            }
          } catch (sessionCheckError) {
            console.warn('⚠️ loginWithEmail: セッション確認エラー:', sessionCheckError);
          }

          // ユーザープロファイル取得
          const userProfile = await this.getUserProfile(data.user.id);
          if (userProfile) {
            // 会社名を取得（company_idから）
            let companyName = ''; // ラクハイ管理者の場合は空文字
            if (userProfile.company_id) {
              try {
                if (import.meta.env.DEV) {
                  console.log('🔍 loginWithEmail: 会社名取得開始:', userProfile.company_id);
                }
                const { data: companyData, error: companyError } = await this.supabase
                  .from('companies')
                  .select('name')
                  .eq('id', userProfile.company_id)
                  .single();

                if (companyError) {
                  console.warn('⚠️ loginWithEmail: 会社名取得エラー:', companyError);
                  // エラーの場合は空文字のまま
                } else if (companyData) {
                  companyName = companyData.name;
                  if (import.meta.env.DEV) {
                    console.log('✅ loginWithEmail: 会社名取得成功:', companyName);
                  }
                } else {
                  console.warn(
                    '⚠️ loginWithEmail: 会社データが見つかりません:',
                    userProfile.company_id
                  );
                  // 会社が見つからない場合もフォールバック処理は empty string で OK
                  // Header コンポーネントで「会社名未設定」と表示される
                }
              } catch (companyFetchError) {
                console.error('❌ loginWithEmail: 会社名取得中にエラー:', companyFetchError);
                // エラーの場合は空文字のまま
              }
            }

            // Userオブジェクトに変換
            const user: User = {
              id: data.user.id,
              email: email,
              name: userProfile.name,
              role: this.mapDatabaseRoleToEnum(userProfile.role),
              company: companyName,
              companyId: userProfile.company_id,
              landTransporterId: userProfile.land_transporter_id,
            };

            // 認証成功時のlocalStorage保存
            try {
              localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
              if (import.meta.env.DEV) {
                console.log('✅ 認証状態をlocalStorageに保存');
              }
            } catch (storageError) {
              console.warn('認証状態の保存に失敗:', storageError);
            }
            return user;
          }
        }

        // Supabase認証が失敗した場合、エラーをログに記録して続行
        if (error) {
          console.warn('⚠️ Supabase認証失敗:', error.message);
        }
      } catch (supabaseError) {
        console.warn('⚠️ Supabase認証エラー:', supabaseError);
      }
    } else {
      console.warn('⚠️ Supabaseクライアントが初期化されていません');
    }

    // 2. Supabaseが利用できない場合のフォールバック認証
    if (!this.supabase) {
      console.warn('⚠️ フォールバック認証を試行');
      return this.fallbackEmailAuth(email, password);
    }

    // 3. 高速ログイン: localStorage認証確認（既にSupabase認証済みの場合はスキップ）
    try {
      const storedUserData = localStorage.getItem(AUTH_STORAGE_KEY);
      if (storedUserData) {
        const cachedUser: User = JSON.parse(storedUserData);
        if (cachedUser && cachedUser.name && cachedUser.role && cachedUser.email === email) {
          if (import.meta.env.DEV) {
            console.log('🚀 loginWithEmail: localStorage高速ログイン使用', {
              name: cachedUser.name,
              role: cachedUser.role,
            });
          }

          // バックグラウンドでSupabase認証検証（ネットワークエラーは無視）
          if (this.supabase) {
            setTimeout(async () => {
              try {
                await this.supabase!.auth.signInWithPassword({ email, password });
                if (import.meta.env.DEV) {
                  console.log('✅ loginWithEmail: バックグラウンドSupabase認証完了');
                }
              } catch (bgError) {
                console.warn('⚠️ loginWithEmail: バックグラウンド認証失敗（継続使用可）:', bgError);
              }
            }, 100);
          }

          return cachedUser; // 即座に認証状態返却
        }
      }
    } catch (parseError) {
      console.warn('localStorage認証データ解析失敗:', parseError);
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }

    // 4. 最終的にエラーを返す
    throw new DataServiceError(
      'メールアドレスまたはパスワードが正しくありません',
      ErrorCodes.AUTHENTICATION_FAILED
    );
  }

  /**
   * Supabase接続エラー時のフォールバック認証
   * デモ用の認証情報でログインを試行
   */
  private async fallbackEmailAuth(email: string, password: string): Promise<User> {
    if (import.meta.env.DEV) {
      console.log('🔄 フォールバック認証開始:', { email });
    }

    // デモ用認証情報（環境変数から取得）
    const demoPassword = import.meta.env.VITE_DEMO_PASSWORD || 'demo-password-2024';
    const demoUsers = {
      'admin@sample-rikuso.co.jp': {
        password: demoPassword,
        user: {
          id: 'demo-admin-001',
          email: 'admin@sample-rikuso.co.jp',
          name: 'サンプル管理者',
          role: Role.ADMIN,
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
        } as User,
      },
      'dispatcher@sample-rikuso.co.jp': {
        password: demoPassword,
        user: {
          id: 'demo-dispatcher-001',
          email: 'dispatcher@sample-rikuso.co.jp',
          name: 'サンプル配車担当',
          role: Role.DISPATCHER,
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
        } as User,
      },
      'driver@sample-rikuso.co.jp': {
        password: demoPassword,
        user: {
          id: 'demo-driver-001',
          email: 'driver@sample-rikuso.co.jp',
          name: 'サンプル陸送員A',
          role: Role.DRIVER,
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
          landTransporterId: 'demo-driver-001',
        } as User,
      },
      'readonly@sample-rikuso.co.jp': {
        password: demoPassword,
        user: {
          id: 'demo-readonly-001',
          email: 'readonly@sample-rikuso.co.jp',
          name: 'サンプル閲覧者',
          role: Role.READ_ONLY,
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
        } as User,
      },
    };

    const demoCredential = demoUsers[email as keyof typeof demoUsers];

    if (!demoCredential) {
      throw new DataServiceError(
        'このメールアドレスは登録されていません',
        ErrorCodes.AUTHENTICATION_FAILED
      );
    }

    if (demoCredential.password !== password) {
      throw new DataServiceError('パスワードが正しくありません', ErrorCodes.AUTHENTICATION_FAILED);
    }

    if (import.meta.env.DEV) {
      console.log('✅ フォールバック認証成功:', {
        email,
        name: demoCredential.user.name,
        role: demoCredential.user.role,
      });
    }

    // フォールバック認証成功時もlocalStorageに保存
    try {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(demoCredential.user));
      if (import.meta.env.DEV) {
        console.log('✅ フォールバック認証状態をlocalStorageに保存');
      }
    } catch (storageError) {
      console.warn('フォールバック認証状態の保存に失敗:', storageError);
    }

    return demoCredential.user;
  }

  async logout(): Promise<void> {
    try {
      // 1. Supabaseログアウト（Supabaseが利用可能な場合）
      if (this.supabase) {
        const { error } = await this.supabase.auth.signOut();
        if (error) {
          throw new DataServiceError(
            `ログアウトに失敗しました: ${error.message}`,
            ErrorCodes.AUTHENTICATION_FAILED
          );
        }
        if (import.meta.env.DEV) {
          console.log('Supabaseログアウト完了');
        }
      }

      // 2. localStorageから認証状態を削除（デモモード・リロード対応）
      try {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        if (import.meta.env.DEV) {
          console.log('localStorage認証状態削除完了');
        }
      } catch (storageError) {
        console.warn('Failed to clear auth state from localStorage:', storageError);
        // localStorageエラーは警告のみで処理続行
      }
    } catch (error) {
      console.error('Logout error:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'ログアウトに失敗しました',
        ErrorCodes.AUTHENTICATION_FAILED,
        error as Error
      );
    }
  }

  // デバッグ用: データベース状況確認
  async debugDatabaseStatus(): Promise<void> {
    if (!this.supabase) {
      if (import.meta.env.DEV) {
        console.log('❌ Supabaseクライアントが初期化されていません');
      }
      return;
    }

    if (import.meta.env.DEV) {
      console.log('🔍 データベース状況デバッグ開始');
    }

    try {
      // 1. user_profilesテーブルの確認
      if (import.meta.env.DEV) {
        console.log('🔍 user_profilesテーブル確認中...');
      }
      const { data: profiles, error: profileError } = await this.supabase
        .from('user_profiles')
        .select('*');
      if (import.meta.env.DEV) {
        console.log('🔍 user_profiles結果:', {
          profileCount: profiles?.length || 0,
          error: profileError?.message,
          errorCode: profileError?.code,
          profiles,
        });
      }

      // 2. companiesテーブルの確認
      if (import.meta.env.DEV) {
        console.log('🔍 companiesテーブル確認中...');
      }
      const { data: companies, error: companyError } = await this.supabase
        .from('companies')
        .select('*');
      if (import.meta.env.DEV) {
        console.log('🔍 companies結果:', {
          companyCount: companies?.length || 0,
          error: companyError?.message,
          errorCode: companyError?.code,
          companies,
        });
      }

      // 3. 特定ユーザーの確認 (wakatchi.watch@gmail.com)
      if (import.meta.env.DEV) {
        console.log('🔍 特定ユーザー確認中...');
      }
      const {
        data: { user },
        error: userError,
      } = await this.supabase.auth.getUser();
      if (import.meta.env.DEV) {
        console.log('🔍 現在のユーザー:', {
          userId: user?.id,
          email: user?.email,
          error: userError?.message,
        });
      }

      if (user) {
        const { data: userProfile, error: userProfileError } = await this.supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        if (import.meta.env.DEV) {
          console.log('🔍 ユーザープロファイル:', {
            profile: userProfile,
            error: userProfileError?.message,
            errorCode: userProfileError?.code,
          });
        }
      }
    } catch (error) {
      console.error('❌ データベース状況確認エラー:', error);
    }

    if (import.meta.env.DEV) {
      console.log('🔍 データベース状況デバッグ完了');
    }
  }

  async getCurrentUser(): Promise<User | null> {
    // 1. Supabase認証の確認（Supabaseが利用可能な場合）
    if (this.supabase) {
      try {
        if (import.meta.env.DEV) {
          console.log('🔍 getCurrentUser: Supabase認証ユーザー取得開始');
        }

        const {
          data: { user },
          error,
        } = await this.supabase.auth.getUser();

        if (import.meta.env.DEV) {
          console.log('🔍 getCurrentUser auth.getUser結果:', {
            user: user ? { id: user.id, email: user.email } : null,
            error: error?.message,
          });
        }

        if (!error && user) {
          if (import.meta.env.DEV) {
            console.log('🔍 getCurrentUser: ユーザープロファイル取得開始:', user.id);
          }

          try {
            // ユーザープロファイル取得
            const userProfile = await this.getUserProfile(user.id);

            if (import.meta.env.DEV) {
              console.log('🔍 getCurrentUser: プロファイル取得成功:', userProfile);
            }

            // 会社名を取得（company_idから）
            let companyName = ''; // ラクハイ管理者の場合は空文字
            if (userProfile.company_id) {
              try {
                if (import.meta.env.DEV) {
                  console.log('🔍 getCurrentUser: 会社名取得開始:', userProfile.company_id);
                }
                const { data: companyData, error: companyError } = await this.supabase
                  .from('companies')
                  .select('name')
                  .eq('id', userProfile.company_id)
                  .single();

                if (companyError) {
                  console.warn('⚠️ getCurrentUser: 会社名取得エラー:', companyError);
                  // エラーの場合は空文字のまま
                } else if (companyData) {
                  companyName = companyData.name;
                  if (import.meta.env.DEV) {
                    console.log('✅ getCurrentUser: 会社名取得成功:', companyName);
                  }
                } else {
                  console.warn(
                    '⚠️ getCurrentUser: 会社データが見つかりません:',
                    userProfile.company_id
                  );
                  // 会社が見つからない場合もフォールバック処理は empty string で OK
                  // Header コンポーネントで「会社名未設定」と表示される
                }
              } catch (companyFetchError) {
                console.error('❌ getCurrentUser: 会社名取得中にエラー:', companyFetchError);
                // エラーの場合は空文字のまま
              }
            }

            const supabaseUser: User = {
              id: user.id,
              name: userProfile.name,
              role: userProfile.role as Role,
              company: companyName, // 会社名を設定（空文字の場合もある）
              companyId: userProfile.company_id,
              landTransporterId: userProfile.land_transporter_id,
            };

            // Supabase認証成功時もlocalStorageに保存（リロード対応）
            try {
              localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(supabaseUser));
              if (import.meta.env.DEV) {
                console.log('✅ getCurrentUser: Supabase認証状態をlocalStorageに保存');
              }
            } catch (storageError) {
              console.warn('Failed to save Supabase auth state to localStorage:', storageError);
            }

            return supabaseUser;
          } catch (profileError) {
            console.error('❌ ユーザープロファイル取得エラー:', profileError);
            // プロファイル取得エラーの場合もlocalStorageフォールバックを試行
          }
        } else {
          if (error) {
            console.error('❌ Supabase認証確認エラー:', error.message);
          } else {
            if (import.meta.env.DEV) {
              console.log('🔍 getCurrentUser: Supabaseユーザーなし');
            }
          }
        }
      } catch (supabaseError) {
        console.error('❌ Supabase getCurrentUser error:', supabaseError);
      }
    } else {
      if (import.meta.env.DEV) {
        console.log('🔍 getCurrentUser: Supabaseクライアントなし');
      }
    }

    // 2. localStorageからの認証状態復元（フォールバック・デモモード対応）
    try {
      if (import.meta.env.DEV) {
        console.log('🔍 getCurrentUser: localStorage復元を試行');
      }
      const storedUserData = localStorage.getItem(AUTH_STORAGE_KEY);

      if (storedUserData) {
        const user: User = JSON.parse(storedUserData);

        // データ整合性チェック
        if (user && user.name && user.role) {
          if (import.meta.env.DEV) {
            console.log('✅ getCurrentUser: localStorage認証状態復元成功:', {
              name: user.name,
              role: user.role,
              company: user.company,
            });
          }
          return user;
        } else {
          console.warn('⚠️ getCurrentUser: localStorage認証データが不正:', user);
          // 不正なデータの場合は削除
          localStorage.removeItem(AUTH_STORAGE_KEY);
        }
      } else {
        if (import.meta.env.DEV) {
          console.log('🔍 getCurrentUser: localStorageに認証データなし');
        }
      }
    } catch (storageError) {
      console.error('❌ localStorage認証状態復元エラー:', storageError);
      // localStorageエラーの場合はクリア
      try {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      } catch (clearError) {
        console.warn('localStorage clear failed:', clearError);
      }
    }

    // 3. どちらも失敗した場合
    if (import.meta.env.DEV) {
      console.log('🔍 getCurrentUser: 認証ユーザーが見つかりません');
    }
    return null;
  }

  private async getUserProfile(userId: string, retryCount: number = 0): Promise<any> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }

    const isRetry = retryCount > 0;
    const logPrefix = isRetry ? `🔄 getUserProfile (retry ${retryCount})` : '🔍 getUserProfile';

    const _queryStartTime = performance.now();

    try {
      // タイムアウト付きクエリの実装（短縮版）
      const queryPromise = this.supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      // ⚠️ タイムアウト値延長: ネットワーク遅延対応（ローカル→Supabaseクラウド）
      // グローバルタイムアウト(30秒)より短くする必要がある
      const timeoutDuration = retryCount === 0 ? 25000 : 35000; // 初回25秒、リトライ35秒
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`getUserProfile query timeout after ${timeoutDuration / 1000} seconds`));
        }, timeoutDuration);
      });

      const { data, error } = (await Promise.race([queryPromise, timeoutPromise])) as any;

      // パフォーマンス監視用（将来の分析に使用可能）
      // const _queryElapsedTime = performance.now() - _queryStartTime;

      if (error) {
        console.error(`❌ ${logPrefix} エラー詳細:`, {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          timestamp: new Date().toISOString(),
        });

        // PGRST116 = テーブルが見つからない、PGRST301 = RLSで拒否された場合
        if (error.code === 'PGRST116') {
          throw new DataServiceError(
            'user_profilesテーブルが存在しません。データベースの移行を確認してください。',
            ErrorCodes.DATA_ACCESS_ERROR
          );
        }

        if (error.code === 'PGRST301' || error.message.includes('RLS')) {
          throw new DataServiceError(
            'ユーザープロファイルへのアクセスが拒否されました。権限設定を確認してください。',
            ErrorCodes.AUTHENTICATION_FAILED
          );
        }

        // リトライ可能なエラーかチェック
        if (retryCount === 0 && this.shouldRetryGetUserProfile(error)) {
          console.warn(`⚠️ ${logPrefix}: リトライ可能なエラー、1秒後にリトライします`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // 1秒待機
          return this.getUserProfile(userId, retryCount + 1);
        }

        throw new DataServiceError(
          `ユーザープロファイルの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      if (!data) {
        // データなしの場合もリトライ対象
        if (retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // 1秒待機
          return this.getUserProfile(userId, retryCount + 1);
        }

        throw new DataServiceError(
          'ユーザープロファイルが見つかりません',
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      return data;
    } catch (error: any) {
      console.error(`❌ ${logPrefix}: 予期しないエラー:`, {
        error: error.message,
        stack: error.stack,
        userId,
        retryCount,
        timestamp: new Date().toISOString(),
      });

      if (error instanceof DataServiceError) {
        throw error;
      }

      // タイムアウトエラーのリトライ判定
      if (error.message.includes('timeout')) {
        if (retryCount === 0) {
          console.warn(`⚠️ ${logPrefix}: タイムアウト、2秒後にリトライします`);
          await new Promise(resolve => setTimeout(resolve, 2000)); // 2秒待機
          return this.getUserProfile(userId, retryCount + 1);
        }

        throw new DataServiceError(
          'ユーザープロファイルの取得がタイムアウトしました。ネットワーク接続またはデータベースの状態を確認してください。',
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      throw new DataServiceError(
        `ユーザープロファイルの取得中に予期しないエラーが発生しました: ${error.message}`,
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }
  }

  /**
   * データベースのrole値をUI用Role enumに変換
   */
  private mapDatabaseRoleToEnum(dbRole: string): Role {
    const roleMapping: Record<string, Role> = {
      super_admin: Role.SUPER_ADMIN,
      ラクハイ管理者: Role.SUPER_ADMIN,
      admin: Role.ADMIN,
      陸送会社管理者: Role.ADMIN,
      dispatcher: Role.DISPATCHER,
      '配車・営業担当者': Role.DISPATCHER,
      driver: Role.DRIVER,
      陸送員: Role.DRIVER,
      manager: Role.MANAGER,
      マネージャー: Role.MANAGER,
      read_only: Role.READ_ONLY,
      閲覧専用: Role.READ_ONLY,
    };

    const mappedRole = roleMapping[dbRole];
    if (!mappedRole) {
      console.warn('🔧 Unknown role from database, defaulting to READ_ONLY:', dbRole);
      return Role.READ_ONLY;
    }

    console.log('🔧 Role mapping:', { dbRole, mappedRole });
    return mappedRole;
  }

  // getUserProfileのリトライ判定
  private shouldRetryGetUserProfile(error: any): boolean {
    // ネットワーク関連エラー、一時的なデータベースエラーをリトライ対象とする
    if (!error) return false;

    const retryableCodes = [
      'PGRST500', // Internal Server Error
      'PGRST503', // Service Unavailable
      '23505', // unique_violation (一時的な競合)
    ];

    const retryableMessages = ['network', 'connection', 'timeout', 'temporary', 'unavailable'];

    // エラーコードでの判定
    if (error.code && retryableCodes.includes(error.code)) {
      return true;
    }

    // エラーメッセージでの判定
    if (error.message) {
      const message = error.message.toLowerCase();
      return retryableMessages.some(keyword => message.includes(keyword));
    }

    return false;
  }

  // 新規ユーザー登録
  async signUp(
    email: string,
    password: string,
    userData: {
      name: string;
      role: Role;
      companyId?: string; // super_adminの場合undefinedを許可
      landTransporterId?: string;
    }
  ): Promise<User> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.AUTHENTICATION_FAILED
      );
    }

    try {
      console.log('🚀 signUp開始:', { email, userData });

      // Supabaseユーザー作成
      console.log('🔍 Supabase auth.signUp呼び出し前');
      const { data, error } = await this.supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name,
            role: userData.role,
            company_id: userData.companyId,
            land_transporter_id: userData.landTransporterId,
          },
        },
      });

      console.log('🔍 Supabase auth.signUp レスポンス:', {
        user: data.user?.id,
        error,
        session: data.session ? 'セッションあり' : 'セッションなし',
      });

      if (error) {
        console.error('❌ Auth SignUp error:', error);
        throw new DataServiceError(
          `ユーザー登録に失敗しました: ${error.message}`,
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }

      if (!data.user) {
        console.error('❌ No user returned from auth.signUp');
        throw new DataServiceError('ユーザー作成に失敗しました', ErrorCodes.AUTHENTICATION_FAILED);
      }

      console.log('🔍 ユーザープロファイル作成開始:', data.user.id);

      // ユーザープロファイル作成
      await this.createUserProfile(data.user.id, userData);

      console.log('✅ signUp完了');

      return {
        id: data.user.id,
        name: userData.name,
        role: userData.role,
        company: userData.role === Role.SUPER_ADMIN ? '' : userData.companyId || '未設定',
        companyId: userData.role === Role.SUPER_ADMIN ? undefined : userData.companyId,
        landTransporterId: userData.landTransporterId,
      };
    } catch (error) {
      console.error('Supabase signup error:', error);
      console.error('SignUp userData:', userData);
      console.error(
        'Processed companyId:',
        userData.role === Role.SUPER_ADMIN ? null : userData.companyId || null
      );

      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        `ユーザー登録に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
        ErrorCodes.AUTHENTICATION_FAILED,
        error as Error
      );
    }
  }

  // ユーザープロファイル作成（カスタムクレーム付き）
  private async createUserProfile(
    userId: string,
    userData: {
      name: string;
      role: Role;
      companyId?: string; // super_adminの場合undefinedを許可
      landTransporterId?: string;
    }
  ): Promise<void> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }

    console.log('🔍 createUserProfile開始:', {
      userId,
      userData,
      role: userData.role,
    });

    // データベース関数を使用してプロファイル作成とメタデータ設定を同時実行
    // super_adminの場合はcompany_idをnullに設定
    const companyId = userData.role === Role.SUPER_ADMIN ? null : userData.companyId || null;

    console.log('🔍 RPC関数呼び出し前:', {
      user_id: userId,
      user_name: userData.name,
      user_role: userData.role,
      company_id: companyId,
      land_transporter_id: userData.landTransporterId,
    });

    try {
      // まず、関数の存在を確認
      console.log('🔍 データベース関数を呼び出し中...');

      const { data, error } = await this.supabase.rpc('create_user_profile_with_metadata', {
        user_id: userId,
        user_name: userData.name,
        user_role: userData.role,
        company_id: companyId,
        land_transporter_id: userData.landTransporterId,
      });

      console.log('🔍 RPC関数レスポンス:', { data, error });

      if (error) {
        console.error('❌ Database function error:', error);

        // 関数が存在しない場合の代替実装
        if (error.message?.includes('function') && error.message?.includes('does not exist')) {
          console.log('⚠️ 関数が存在しないため、直接テーブル操作で実装');
          return await this.createUserProfileDirect(userId, userData);
        }

        console.error('❌ Function parameters:', {
          user_id: userId,
          user_name: userData.name,
          user_role: userData.role,
          company_id: companyId,
          land_transporter_id: userData.landTransporterId,
        });

        throw new DataServiceError(
          `ユーザープロファイルの作成に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      console.log('✅ User profile created with custom claims for user:', userId);
    } catch (err) {
      console.error('❌ createUserProfile例外:', err);

      // ネットワーク関連のエラーや予期しないエラーの場合の代替実装
      if (
        err instanceof Error &&
        (err.message.includes('function') || err.message.includes('network'))
      ) {
        console.log('⚠️ エラーのため代替実装を試行');
        return await this.createUserProfileDirect(userId, userData);
      }

      throw err;
    }
  }

  // 代替実装: データベース関数が利用できない場合の直接テーブル操作
  private async createUserProfileDirect(
    userId: string,
    userData: {
      name: string;
      role: Role;
      companyId?: string;
      landTransporterId?: string;
    }
  ): Promise<void> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }

    console.log('🔧 直接テーブル操作でユーザープロファイル作成');

    const companyId = userData.role === Role.SUPER_ADMIN ? null : userData.companyId || null;

    // user_profilesテーブルに直接挿入
    const { error: insertError } = await this.supabase.from('user_profiles').insert({
      id: userId,
      name: userData.name,
      role: userData.role,
      company_id: companyId,
      land_transporter_id: userData.landTransporterId || null,
    });

    if (insertError) {
      console.error('❌ Direct insert error:', insertError);
      throw new DataServiceError(
        `ユーザープロファイルの挿入に失敗しました: ${insertError.message}`,
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }

    console.log('✅ User profile created directly for user:', userId);
  }

  // パスワードリセット
  async resetPassword(email: string): Promise<void> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.AUTHENTICATION_FAILED
      );
    }

    try {
      const { error } = await this.supabase.auth.resetPasswordForEmail(email);
      if (error) {
        throw new DataServiceError(
          `パスワードリセットに失敗しました: ${error.message}`,
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }
    } catch (error) {
      console.error('Password reset error:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'パスワードリセットに失敗しました',
        ErrorCodes.AUTHENTICATION_FAILED,
        error as Error
      );
    }
  }

  // 認証状態監視の設定
  private setupAuthListener(): void {
    if (!this.supabase) return;

    this.supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔍 Auth state changed:', {
        event,
        userId: session?.user?.id,
        email: session?.user?.email,
        hasCallback: !!this.authStateChangeCallback,
      });

      if (this.authStateChangeCallback) {
        let user: User | null = null;

        if (session?.user) {
          try {
            console.log('🔍 Auth listener: ユーザープロファイル取得開始');

            // ユーザープロファイル取得
            const userProfile = await this.getUserProfile(session.user.id);
            user = {
              id: session.user.id,
              name: userProfile.name,
              role: this.mapDatabaseRoleToEnum(userProfile.role),
              company: userProfile.company_id || '',
              companyId: userProfile.company_id,
              landTransporterId: userProfile.land_transporter_id,
            };

            // Auth state change時もlocalStorageに保存（リロード対応）
            try {
              localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
              console.log('✅ Auth listener: 認証状態をlocalStorageに保存');
            } catch (storageError) {
              console.warn('Failed to save auth state change to localStorage:', storageError);
            }
          } catch (error) {
            console.error('Failed to load user profile on auth state change:', error);
            user = null;
          }
        } else if (event === 'SIGNED_OUT') {
          // ログアウト時はlocalStorageもクリア
          try {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            console.log('✅ Auth listener: ログアウト時にlocalStorageクリア');
          } catch (storageError) {
            console.warn('Failed to clear localStorage on sign out:', storageError);
          }
        }

        this.authStateChangeCallback(user);
      }
    });
  }

  // 認証状態変更のコールバックを設定（AuthContextから使用）
  onAuthStateChange(callback: (user: User | null) => void): (() => void) | null {
    this.authStateChangeCallback = callback;
    // Supabaseの場合は明示的なunsubscribe関数を返す必要がない（setupAuthListenerで管理）
    return null;
  }

  // セッション取得（デバッグ用）
  async getSession() {
    if (!this.supabase) return null;
    const {
      data: { session },
    } = await this.supabase.auth.getSession();
    return session;
  }
}

// Supabaseマスターデータサービス
export class SupabaseMasterDataService implements MasterDataService {
  private supabase: SupabaseClient | null = null;
  private currentCompanyId: string | null = null;
  private isDemo: boolean;
  private readonly DEMO_COMPANY_ID = 'e32a0499-6456-44ac-9b16-b0001098c259';

  /**
   * 現在の会社IDを取得
   */
  private getCurrentCompanyId(): string {
    if (this.isDemo) {
      return this.DEMO_COMPANY_ID;
    }

    // 通常モードでは認証されたユーザーの会社IDを取得
    if (this.currentCompanyId) {
      return this.currentCompanyId;
    }

    // フォールバック: localStorageから取得を試行
    try {
      const userDataStr = localStorage.getItem(AUTH_STORAGE_KEY);
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        if (userData.companyId) {
          return userData.companyId;
        }
      }
    } catch (error) {
      console.warn('⚠️ ユーザーデータ取得エラー:', error);
    }

    // 最終フォールバック: デモ会社ID
    console.warn('⚠️ 会社IDが特定できません。デモ会社IDを使用します。');
    return this.DEMO_COMPANY_ID;
  }

  constructor(demoMode?: boolean) {
    this.isDemo = demoMode ?? isDemoMode();
    this.initializeSupabase();

    // デモモード時はデモ会社IDを自動設定
    if (this.isDemo) {
      this.currentCompanyId = this.DEMO_COMPANY_ID;
      console.log('🎭 SupabaseMasterDataService: デモモードで初期化 - デモ会社ID設定');
    }
  }

  private initializeSupabase() {
    try {
      const config = getSupabaseConfig();

      console.log('[SupabaseService] 初期化試行中...', {
        hasUrl: !!config.url,
        hasAnonKey: !!config.anonKey,
        urlLength: config.url?.length || 0,
        anonKeyLength: config.anonKey?.length || 0,
      });

      if (!config.url || !config.anonKey) {
        console.warn(
          '[SupabaseService] Supabaseの設定が見つかりません。環境変数を確認してください。',
          config
        );
        return;
      }

      // シングルトンSupabaseクライアントを使用
      this.supabase = getSupabaseClient();
      console.log('✅ SupabaseMasterDataService: シングルトンクライアント取得完了');
    } catch (error) {
      console.error('[SupabaseService] Supabase MasterDataService initialization failed:', error);
    }
  }

  /**
   * Supabaseクライアントの存在を確認し、存在しない場合はエラーを投げる
   */
  private ensureSupabaseClient(): SupabaseClient {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.CLIENT_INIT_FAILED
      );
    }
    return this.supabase;
  }

  /**
   * 現在の認証ユーザーを取得（MasterDataServiceメソッド用）
   */
  private async getCurrentUser(): Promise<User> {
    try {
      // デモモード時は専用の認証確認を実行
      if (isDemoMode()) {
        // AuthContextから現在のユーザー情報を取得
        const authKey = 'fleetpilot_demo_current_user';
        const storedDemoUser = localStorage.getItem(authKey);

        if (storedDemoUser) {
          try {
            const demoUser = JSON.parse(storedDemoUser);
            return demoUser as User;
          } catch (parseError) {
            console.warn('⚠️ SupabaseMasterDataService: デモユーザー情報解析失敗:', parseError);
          }
        }

        // localStorage にデモユーザー情報がない場合はデフォルトのデモユーザーで継続
        console.warn('⚠️ デモユーザー情報が未設定のため、デフォルトユーザーを使用します');
        return {
          id: 'demo-user',
          email: 'demo@example.com',
          name: 'Demo User',
          role: Role.ADMIN,
          company: 'Demo Company',
          companyId: this.DEMO_COMPANY_ID,
        } as User;
      }

      // 本番モード時は従来のSupabaseAuthServiceを使用
      const authService = new SupabaseAuthService();
      const user = await authService.getCurrentUser();

      if (!user) {
        throw new DataServiceError(
          '認証ユーザーが見つかりません',
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }

      return user;
    } catch (error) {
      // 認証エラーの場合は詳細ログを出力せず、シンプルにエラーを投げ直す
      throw error instanceof DataServiceError
        ? error
        : new DataServiceError('認証状態の確認に失敗しました', ErrorCodes.AUTHENTICATION_FAILED);
    }
  }

  // ========================================
  // マルチテナント対応: 会社管理メソッド
  // ========================================

  setCurrentCompany(companyId: string) {
    this.currentCompanyId = companyId;
  }

  getCurrentCompany(): string | null {
    return this.currentCompanyId;
  }

  /**
   * システム設定画面用：全ての取引先を取得（会社フィルタなし）
   * 陸送会社管理者がシステム設定で全取引先から選択できるようにする
   * @returns 全取引先データ（49件）
   */
  async getAllBusinessPartners(): Promise<BusinessPartner[]> {
    console.log('🎯 SupabaseService: getAllBusinessPartners開始 - 全取引先取得（フィルタなし）');

    if (!this.supabase) {
      console.warn('Supabase未設定のため、会社固有取引先の取得をスキップし空配列を返却します');
      return [];
    }

    try {
      // 1. 全取引先データを取得（会社フィルタなし）
      // TODO: 将来的に大規模データセット対応のため、ページネーション機能を追加することを検討
      // 現状は49件と少ないため、パフォーマンス上の問題はない
      const { data: businessPartnersData, error } = await this.supabase
        .from('business_partners')
        .select('*')
        .eq('status', 'Active')
        .order('name');

      if (error) {
        throw new DataServiceError(
          `取引先データの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      const businessPartners = (businessPartnersData || []).map((partner: any) => ({
        id: partner.id,
        name: partner.name,
        displayName: partner.display_name || partner.name,
        type: partner.type as BusinessPartnerType,
        category: partner.category as BusinessPartnerCategory,
        availableRoles: partner.available_roles || [],
        defaultAddress: partner.default_address,
        defaultPhone: partner.default_phone,
        defaultEmail: partner.default_email || '',
        defaultContactPerson: partner.default_contact_person || '',
        notes: partner.notes || '',
        status: partner.status,
        companyId: partner.company_id,
        dataSource: partner.data_source as BusinessPartnerDataSource,
        locations: partner.locations || [],
        billingInfo: partner.billing_info || null,
        isSystemPreset: partner.is_system_preset || false,
        createdAt: partner.created_at,
        updatedAt: partner.updated_at,
      }));

      console.log(
        `✅ SupabaseService: getAllBusinessPartners完了 - ${businessPartners.length}件取得`
      );
      console.log('📊 データソース内訳:', {
        rakuhaiProvided: businessPartners.filter(
          p => p.dataSource === BusinessPartnerDataSource.RAKUHAI_PROVIDED
        ).length,
        custom: businessPartners.filter(p => p.dataSource === BusinessPartnerDataSource.CUSTOM)
          .length,
      });

      return businessPartners;
    } catch (error) {
      console.error('❌ SupabaseService: getAllBusinessPartners失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '全取引先データの取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 会社固有の取引先を取得（フィルタリング適用）
   * MasterDataContext.refreshBusinessPartnersで使用されるメソッド
   * @returns フィルタリング済みの取引先データ
   */
  async getBusinessPartners(): Promise<BusinessPartner[]> {
    // キャッシュを無効化して常に最新データを取得
    if (typeof window !== 'undefined') {
      const { CacheManager } = await import('../utils/cacheManager');
      CacheManager.invalidate('business-partners');
      CacheManager.invalidate('supabaseService');
      console.log('🗑️ SupabaseMasterDataService: ビジネスパートナーキャッシュを無効化');
    }

    console.log('🎯 SupabaseMasterDataService: getBusinessPartners開始 - 会社固有取引先取得');

    if (!this.supabase) {
      console.warn('Supabase未設定のため、会社固有取引先の取得をスキップし空配列を返却します');
      return [];
    }

    try {
      const companyId = this.getCurrentCompanyId();
      console.log('🔧 SupabaseMasterDataService: 使用会社ID', {
        companyId,
        isDemoMode: this.isDemo,
      });

      // デモモード時は専用のフィルタリング
      if (this.isDemo && companyId === this.DEMO_COMPANY_ID) {
        console.log('🎭 デモモード: デモ会社の取引先を取得');

        const { data: businessPartnersData, error } = await this.supabase
          .from('business_partners')
          .select('*')
          .eq('status', 'Active')
          .order('name');

        if (error) {
          throw new DataServiceError(
            `取引先データの取得に失敗しました: ${error.message}`,
            ErrorCodes.DATA_ACCESS_ERROR,
            error
          );
        }

        const businessPartners = (businessPartnersData || []).map((partner: any) => ({
          id: partner.id,
          name: partner.name,
          displayName: partner.display_name || partner.name,
          type: partner.type as BusinessPartnerType,
          category: partner.category as BusinessPartnerCategory,
          availableRoles: partner.available_roles || [],
          defaultAddress: partner.default_address,
          defaultPhone: partner.default_phone,
          defaultEmail: partner.default_email || '',
          defaultContactPerson: partner.default_contact_person || '',
          notes: partner.notes || '',
          status: partner.status,
          companyId: partner.company_id,
          dataSource: partner.data_source as BusinessPartnerDataSource,
          locations: partner.locations || [],
          billingInfo: partner.billing_info || null,
          isSystemPreset: partner.is_system_preset || false,
          createdAt: partner.created_at,
          updatedAt: partner.updated_at,
        }));

        console.log(
          `✅ SupabaseMasterDataService: getBusinessPartners完了 - ${businessPartners.length}件取得（デモモード）`
        );
        return businessPartners;
      }

      // 通常モード時はgetAllBusinessPartnersと同じ処理
      return await this.getAllBusinessPartners();
    } catch (error) {
      console.error('❌ SupabaseMasterDataService: getBusinessPartners失敗:', error);
      // セキュリティ重視: フェイルセーフで空配列を返す（テスト要件に合わせる）
      return [];
    }
  }

  async getCompanies(): Promise<Company[]> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.UNKNOWN_ERROR
      );
    }

    try {
      const { data, error } = await this.supabase.from('companies').select('*').order('name');

      if (error) {
        throw new DataServiceError(
          `会社データの取得に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }

      return (data || []).map((company: any) => ({
        id: company.id,
        name: company.name,
        code: company.code,
        address: company.address,
        phone: company.phone,
        email: company.email || undefined,
        contactPerson: company.contact_person || undefined,
        // FL-184: statusカラムは削除され、subscriptionStatusに統合
        subscriptionPlan: company.subscription_plan,
        subscriptionStatus: company.subscription_status,
        maxUsers: company.max_users,
        maxDrivers: company.max_drivers,
        maxVehicles: company.max_vehicles,
        settings: company.settings || {},
        createdAt: company.created_at,
        updatedAt: company.updated_at,
      }));
    } catch (error) {
      throw new DataServiceError(
        '会社データの取得中にエラーが発生しました',
        ErrorCodes.UNKNOWN_ERROR,
        error as Error
      );
    }
  }

  async createCompany(companyData: Omit<Company, 'id'>): Promise<Company> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.UNKNOWN_ERROR
      );
    }

    try {
      const { data, error } = await this.supabase
        .from('companies')
        .insert([
          {
            name: companyData.name,
            code: companyData.code,
            address: companyData.address,
            phone: companyData.phone,
            email: companyData.email,
            contact_person: companyData.contactPerson,
            subscription_plan: companyData.subscriptionPlan,
            subscription_status: companyData.subscriptionStatus,
            max_users: companyData.maxUsers,
            max_drivers: companyData.maxDrivers,
            max_vehicles: companyData.maxVehicles,
            settings: companyData.settings,
          },
        ])
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          `会社の作成に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }

      const companies = await this.getCompanies();
      const createdCompany = companies.find(c => c.id === data.id);
      if (!createdCompany) {
        throw new DataServiceError('作成された会社が見つかりません', ErrorCodes.UNKNOWN_ERROR);
      }

      return createdCompany;
    } catch (error) {
      throw new DataServiceError(
        '会社の作成中にエラーが発生しました',
        ErrorCodes.UNKNOWN_ERROR,
        error as Error
      );
    }
  }

  // ========================================
  // マルチテナント対応: 案件管理メソッド
  // ========================================

  async getCompanyCases(companyId?: string): Promise<CompanyCase[]> {
    const startTime = performance.now();

    logger.info('🎯 getCompanyCases: メソッド開始', {
      providedCompanyId: companyId,
      timestamp: new Date().toISOString(),
    });

    if (!this.supabase) {
      logger.error('❌ getCompanyCases: Supabaseクライアントが初期化されていません');
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.UNKNOWN_ERROR
      );
    }

    const targetCompanyId = companyId || this.getCurrentCompanyId();
    logger.info('🔍 getCompanyCases: companyId検証', {
      providedCompanyId: companyId,
      targetCompanyId,
      getCurrentCompanyIdResult: this.getCurrentCompanyId(),
    });

    if (!targetCompanyId) {
      logger.error('❌ getCompanyCases: 会社IDが指定されていません');
      throw new DataServiceError('会社IDが指定されていません', ErrorCodes.UNKNOWN_ERROR);
    }

    try {
      logger.info('🚀 getCompanyCases: Supabaseクエリ実行', {
        table: 'company_cases',
        targetCompanyId,
        orderBy: 'created_at DESC',
      });

      const { data, error } = await this.supabase
        .from('company_cases')
        .select('*')
        .eq('company_id', targetCompanyId)
        .order('created_at', { ascending: false });

      const queryElapsedTime = performance.now() - startTime;

      if (error) {
        logger.error('❌ getCompanyCases: Supabaseエラー', {
          error: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          targetCompanyId,
          elapsedTime: `${queryElapsedTime.toFixed(2)}ms`,
        });
        throw new DataServiceError(
          `案件データの取得に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }

      logger.info('✅ getCompanyCases: Supabaseクエリ成功', {
        targetCompanyId,
        dataCount: data?.length || 0,
        elapsedTime: `${queryElapsedTime.toFixed(2)}ms`,
        sampleIds: (data || []).slice(0, 3).map(c => c.id),
      });

      return (data || []).map((case_: any) => ({
        id: case_.id,
        companyId: case_.company_id,
        caseNumber: case_.case_number || undefined,
        title: case_.title || undefined,
        status: case_.status,
        priority: case_.priority,

        // 取引先情報
        billingPartnerId: case_.billing_partner_id || undefined,
        billingLocationId: case_.billing_location_id || undefined,
        billingAddressCustom: case_.billing_address_custom || undefined,

        pickupPartnerId: case_.pickup_partner_id || undefined,
        pickupLocationId: case_.pickup_location_id || undefined,
        pickupAddressCustom: case_.pickup_address_custom || undefined,
        pickupPhone: case_.pickup_phone || undefined,
        pickupDate: case_.pickup_date,
        pickupDateEnd: case_.pickup_date_end || undefined,
        pickupTimeStart: case_.pickup_time_start || undefined,
        pickupTimeEnd: case_.pickup_time_end || undefined,

        deliveryPartnerId: case_.delivery_partner_id || undefined,
        deliveryLocationId: case_.delivery_location_id || undefined,
        deliveryAddressCustom: case_.delivery_address_custom || undefined,
        deliveryPhone: case_.delivery_phone || undefined,
        deliveryDate: case_.delivery_date,
        deliveryDateEnd: case_.delivery_date_end || undefined,
        deliveryTime: case_.delivery_time || undefined,

        // 車両・陸送情報
        vehiclePlateNumber: case_.vehicle_plate_number || undefined,
        vehiclePlateNumberNone: case_.vehicle_plate_number_none || false,
        vehicleModel: case_.vehicle_model || undefined,
        vehicleModelId: case_.vehicle_model_id || undefined,
        vehicleDisplayName: case_.vehicle_display_name || undefined,
        vehicleNumber: case_.vehicle_number ?? undefined, // FL-188: 車体番号（VIN）
        chassisNumber: case_.chassis_number || undefined, // FL-191: 車体番号
        assignedLandTransporterId: case_.assigned_transporter_id || undefined,
        transportFee: case_.transport_fee || undefined,

        // 依頼者情報
        requester: case_.requester || undefined,
        managementNumber: case_.management_number || undefined,

        // 進捗情報
        pickupReport: case_.pickup_report || undefined,
        deliveryReport: case_.delivery_report || undefined,

        // FL-113: 車検状態管理
        isVehicleInspectionExpired: case_.is_vehicle_inspection_expired || false,
        requiresTemporaryNumber: case_.requires_temporary_number || false,
        temporaryNumberNotes: case_.temporary_number_notes || undefined,

        // その他
        remarks: case_.remarks || undefined,
        price: case_.price || undefined,

        createdAt: case_.created_at,
        updatedAt: case_.updated_at,
        createdBy: case_.created_by || undefined,
      }));
    } catch (error) {
      const totalElapsedTime = performance.now() - startTime;
      logger.error('❌ getCompanyCases: 予期しないエラー', {
        errorMessage: (error as any)?.message,
        errorStack: (error as any)?.stack,
        totalElapsedTime: `${totalElapsedTime.toFixed(2)}ms`,
      });
      throw new DataServiceError(
        '案件データの取得中にエラーが発生しました',
        ErrorCodes.UNKNOWN_ERROR,
        error as Error
      );
    }
  }

  async createCompanyCase(caseData: Omit<CompanyCase, 'id'>): Promise<CompanyCase> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.UNKNOWN_ERROR
      );
    }

    try {
      const { data, error } = await this.supabase
        .from('company_cases')
        .insert([
          {
            company_id: caseData.companyId,
            case_number: caseData.caseNumber,
            title: caseData.title,
            status: caseData.status,
            priority: caseData.priority,
            billing_partner_id: caseData.billingPartnerId,
            billing_location_id: caseData.billingLocationId,
            billing_address_custom: caseData.billingAddressCustom,
            pickup_partner_id: caseData.pickupPartnerId,
            pickup_location_id: caseData.pickupLocationId,
            pickup_address_custom: caseData.pickupAddressCustom,
            pickup_phone: caseData.pickupPhone,
            pickup_date: caseData.pickupDate,
            pickup_date_end: caseData.pickupDateEnd,
            pickup_time_start: caseData.pickupTimeStart,
            pickup_time_end: caseData.pickupTimeEnd,
            delivery_partner_id: caseData.deliveryPartnerId,
            delivery_location_id: caseData.deliveryLocationId,
            delivery_address_custom: caseData.deliveryAddressCustom,
            delivery_phone: caseData.deliveryPhone,
            delivery_date: caseData.deliveryDate,
            delivery_date_end: caseData.deliveryDateEnd,
            delivery_time: caseData.deliveryTime,
            vehicle_plate_number: caseData.vehiclePlateNumber,
            vehicle_plate_number_none: caseData.vehiclePlateNumberNone,
            vehicle_model: caseData.vehicleModel,
            assigned_transporter_id: caseData.assignedLandTransporterId,
            transport_fee: caseData.transportFee,
            requester: caseData.requester,
            management_number: caseData.managementNumber,
            pickup_report: caseData.pickupReport,
            delivery_report: caseData.deliveryReport,
            remarks: caseData.remarks,
            price: caseData.price,
            created_by: caseData.createdBy,
          },
        ])
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          `案件の作成に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }

      const cases = await this.getCompanyCases(caseData.companyId);
      const createdCase = cases.find(c => c.id === data.id);
      if (!createdCase) {
        throw new DataServiceError('作成された案件が見つかりません', ErrorCodes.UNKNOWN_ERROR);
      }

      return createdCase;
    } catch (error) {
      throw new DataServiceError(
        '案件の作成中にエラーが発生しました',
        ErrorCodes.UNKNOWN_ERROR,
        error as Error
      );
    }
  }

  // ========================================
  // マルチテナント対応: 取引先選択管理メソッド
  // ========================================

  async getCompanyPartnerSelections(companyId?: string): Promise<CompanyPartnerSelection[]> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.UNKNOWN_ERROR
      );
    }

    const targetCompanyId = companyId || this.currentCompanyId;
    if (!targetCompanyId) {
      throw new DataServiceError('会社IDが指定されていません', ErrorCodes.UNKNOWN_ERROR);
    }

    try {
      const { data, error } = await this.supabase
        .from('company_partner_selections')
        .select('*')
        .eq('company_id', targetCompanyId)
        .order('priority');

      if (error) {
        throw new DataServiceError(
          `取引先選択データの取得に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }

      return (data || []).map((selection: any) => ({
        id: selection.id,
        companyId: selection.company_id,
        businessPartnerId: selection.business_partner_id,
        isEnabled: selection.is_enabled,
        roles: selection.roles || [],
        priority: selection.priority,
        customName: selection.custom_name || undefined,
        customNotes: selection.custom_notes || undefined,
        billingAllowed: selection.billing_allowed,
        pickupAllowed: selection.pickup_allowed,
        deliveryAllowed: selection.delivery_allowed,
        usageCount: selection.usage_count,
        lastUsedAt: selection.last_used_at || undefined,
        createdAt: selection.created_at,
        updatedAt: selection.updated_at,
        createdBy: selection.created_by || undefined,
      }));
    } catch (error) {
      throw new DataServiceError(
        '取引先選択データの取得中にエラーが発生しました',
        ErrorCodes.UNKNOWN_ERROR,
        error as Error
      );
    }
  }

  async createCompanyPartnerSelection(
    selectionData: Omit<CompanyPartnerSelection, 'id'>
  ): Promise<CompanyPartnerSelection> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.UNKNOWN_ERROR
      );
    }

    try {
      const { data, error } = await this.supabase
        .from('company_partner_selections')
        .insert([
          {
            company_id: selectionData.companyId,
            business_partner_id: selectionData.businessPartnerId,
            is_enabled: selectionData.isEnabled,
            roles: selectionData.roles,
            priority: selectionData.priority,
            custom_name: selectionData.customName,
            custom_notes: selectionData.customNotes,
            billing_allowed: selectionData.billingAllowed,
            pickup_allowed: selectionData.pickupAllowed,
            delivery_allowed: selectionData.deliveryAllowed,
            usage_count: selectionData.usageCount,
            created_by: selectionData.createdBy,
          },
        ])
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          `取引先選択の作成に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }

      const selections = await this.getCompanyPartnerSelections(selectionData.companyId);
      const createdSelection = selections.find(s => s.id === data.id);
      if (!createdSelection) {
        throw new DataServiceError(
          '作成された取引先選択が見つかりません',
          ErrorCodes.UNKNOWN_ERROR
        );
      }

      return createdSelection;
    } catch (error) {
      throw new DataServiceError(
        '取引先選択の作成中にエラーが発生しました',
        ErrorCodes.UNKNOWN_ERROR,
        error as Error
      );
    }
  }

  // クライアント管理
  // クライアント管理（BusinessPartnerから変換して互換性維持）
  async getClients(): Promise<Client[]> {
    // DEPRECATED: BusinessPartner統合済み - MasterDataContextのgetClientByIdを使用
    console.warn('[DEPRECATED] getClients() - Use MasterDataContext.getClientById() instead');
    return [];
  }

  async createClient(_client: Omit<Client, 'id'>): Promise<Client> {
    throw new DataServiceError('Supabaseクライアント機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  async updateClient(_id: string, _client: Partial<Client>): Promise<Client> {
    throw new DataServiceError('Supabaseクライアント機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  async deleteClient(_id: string): Promise<void> {
    throw new DataServiceError('Supabaseクライアント機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  // 陸送員管理
  async getLandTransporters(): Promise<LandTransporter[]> {
    console.log('🔄 getLandTransporters開始');

    // Supabase未設定時のみフォールバックデータを返す（デモモードでもリモート接続）
    if (!isSupabaseAvailable() || !this.supabase) {
      console.log(
        'ℹ️ SupabaseMasterDataService: Supabase未設定のため、フォールバックデータを返します'
      );
      return DemoDataFallbackService.getLandTransporters();
    }

    const supabase = this.ensureSupabaseClient();

    try {
      // デモモード時は固定のデモ会社IDを使用
      let companyId: string;

      if (isDemoMode()) {
        companyId = 'e32a0499-6456-44ac-9b16-b0001098c259'; // 株式会社サンプル陸送
      } else {
        const user = await this.getCurrentUser();
        if (!user.companyId) {
          console.warn('⚠️ SupabaseMasterDataService: 会社IDが設定されていません');
          return [];
        }
        companyId = user.companyId;
      }

      // company_land_transportersテーブルから詳細な時間情報を含むデータを取得
      const { data, error } = await supabase
        .from('company_land_transporters')
        .select('*')
        .eq('company_id', companyId)
        .order('name', { ascending: true });

      if (error) {
        console.error('❌ SupabaseMasterDataService: 陸送員データ取得エラー:', error);
        throw new DataServiceError(
          `陸送員データの取得に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR
        );
      }

      if (!data || data.length === 0) {
        return [];
      }

      // データベーススキーマからTypeScript型への変換
      const landTransporters: LandTransporter[] = data.map(
        (row: any): LandTransporter => ({
          id: row.id,
          name: row.name,
          phone: row.phone || '',
          address: row.address || '',
          // データベースの値を正規化（大文字小文字を問わず処理）
          status:
            row.status?.toLowerCase() === 'available'
              ? 'Available'
              : row.status?.toLowerCase() === 'busy'
                ? 'Busy'
                : 'Unavailable',
          drivableVehicleTypes: Array.isArray(row.drivable_vehicle_types)
            ? row.drivable_vehicle_types
            : [],
          // working_daysは既に完全形（monday, tuesday等）で詳細な時間情報が保存されている
          workingDays: row.working_days || {
            monday: null,
            tuesday: null,
            wednesday: null,
            thursday: null,
            friday: null,
            saturday: null,
            sunday: null,
          },
          hasLicenseForLargeVehicles: row.has_license_for_large_vehicles || false,
          canWorkNightShift: row.can_work_night_shift || false,
          canWorkLongDistance: row.can_work_long_distance || false,
          hasTemporaryLicense: row.has_temporary_license || false,
          isATOnly: row.is_at_only || false,
          isSolicitationProhibited: row.is_solicitation_prohibited || false,
          isDesignatedStoreProhibited: row.is_designated_store_prohibited || false,
          notes: row.notes || '',
          // FL-270: Google Maps統合 - 最寄駅情報
          nearestStation: row.nearest_station || undefined,
          nearestStationPlaceId: row.nearest_station_place_id || undefined,
          nearestStationLat: row.nearest_station_lat || undefined,
          nearestStationLng: row.nearest_station_lng || undefined,
        })
      );

      console.log('✅ getLandTransporters成功:', landTransporters.length);
      return landTransporters;
    } catch (error) {
      // 認証エラーの場合は空配列を返す（MasterDataContextでの初期化エラー回避）
      if (error instanceof DataServiceError && error.code === ErrorCodes.AUTHENTICATION_FAILED) {
        console.log('ℹ️ SupabaseMasterDataService: 未認証のため陸送員データをスキップ');
        return [];
      }

      console.error('❌ SupabaseMasterDataService: getLandTransporters実行エラー:', error);
      throw error instanceof DataServiceError
        ? error
        : new DataServiceError('陸送員データの取得に失敗しました', ErrorCodes.UNKNOWN_ERROR);
    }
  }

  async createLandTransporter(_transporter: Omit<LandTransporter, 'id'>): Promise<LandTransporter> {
    throw new DataServiceError('Supabase陸送員機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  async updateLandTransporter(
    _id: string,
    _transporter: Partial<LandTransporter>
  ): Promise<LandTransporter> {
    throw new DataServiceError('Supabase陸送員機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  async deleteLandTransporter(_id: string): Promise<void> {
    throw new DataServiceError('Supabase陸送員機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  // 案件管理
  /**
   * @deprecated FL-162: enriched_casesビューは廃止されました。getCompanyCases()を使用してください。
   * 後方互換性のため、内部でgetCompanyCases()を呼び出しています。
   */
  async getCases(): Promise<Case[]> {
    console.warn('⚠️ getCases()は非推奨です。getCompanyCases()を使用してください。');

    // Supabase未設定時のみフォールバックデータを返す（デモモードでもリモート接続）
    if (!isSupabaseAvailable() || !this.supabase) {
      console.log(
        'ℹ️ SupabaseMasterDataService: Supabase未設定のため、フォールバックデータを返します'
      );
      return DemoDataFallbackService.getCases();
    }

    try {
      // デモモード時は固定のデモ会社IDを使用
      let companyId: string;

      if (isDemoMode()) {
        companyId = 'e32a0499-6456-44ac-9b16-b0001098c259'; // 株式会社サンプル陸送
        console.log('🎯 デモモード: 株式会社サンプル陸送の案件データを取得します');
      } else {
        const user = await this.getCurrentUser();
        if (!user.companyId) {
          console.warn('⚠️ SupabaseMasterDataService: 会社IDが設定されていません');
          return [];
        }
        companyId = user.companyId;
      }

      // FL-162: enriched_casesからcompany_casesへ移行
      // 内部でgetCompanyCases()を呼び出し、Case型に変換して返す
      const companyCases = await this.getCompanyCases(companyId);

      // CompanyCase[]をCase[]に変換（共通ユーティリティ関数使用）
      const cases: Case[] = convertCompanyCasesToCases(companyCases);

      console.log('✅ SupabaseMasterDataService: 案件データ取得成功（getCompanyCases経由）', {
        count: cases.length,
        companyId,
      });

      return cases;
    } catch (error) {
      // 認証エラーの場合は空配列を返す（MasterDataContextでの初期化エラー回避）
      if (error instanceof DataServiceError && error.code === ErrorCodes.AUTHENTICATION_FAILED) {
        console.log('ℹ️ SupabaseMasterDataService: 未認証のため案件データをスキップ');
        return [];
      }

      console.error('❌ SupabaseMasterDataService: getCases実行エラー:', error);
      throw error instanceof DataServiceError
        ? error
        : new DataServiceError('案件データの取得に失敗しました', ErrorCodes.UNKNOWN_ERROR);
    }
  }

  async createCase(case_: Omit<Case, 'id'>): Promise<Case> {
    try {
      if (!this.supabase) {
        throw new DataServiceError(
          'Supabaseへの接続が確立されていません',
          ErrorCodes.DATABASE_ERROR
        );
      }

      console.log('🔄 案件作成開始');

      // FL-113: 車検関連フィールドを含めて案件を作成
      const { data, error } = await this.supabase
        .from('company_cases')
        .insert({
          company_id: 'e32a0499-6456-44ac-9b16-b0001098c259', // デモ会社ID
          status: case_.status || 'unassigned',
          assigned_transporter_id: case_.assignedLandTransporterId,
          billing_partner_id: case_.billingPartnerId,
          pickup_partner_id: case_.pickupPartnerId,
          delivery_partner_id: case_.deliveryPartnerId,
          pickup_address_custom: case_.pickupAddress,
          pickup_phone: case_.pickupPhone,
          pickup_date: case_.pickupDate,
          pickup_time_start: case_.pickupTimeStart,
          pickup_time_end: case_.pickupTimeEnd,
          delivery_address_custom: case_.deliveryAddress,
          delivery_phone: case_.deliveryPhone,
          delivery_date: case_.deliveryDate,
          delivery_time: case_.deliveryTime,
          vehicle_model: case_.vehicleModel,
          vehicle_plate_number: case_.vehiclePlateNumber,
          // FL-163: 車体番号 (acceptanceStatus removed - now using status)
          chassis_number: case_.chassisNumber,
          price: case_.price || 0,
          remarks: case_.remarks,
        })
        .select()
        .single();

      if (error) {
        console.error('❌ 案件作成エラー:', error);
        throw new DataServiceError(
          `案件の作成に失敗しました: ${error.message}`,
          ErrorCodes.DATABASE_ERROR
        );
      }

      if (!data) {
        throw new DataServiceError('案件の作成に失敗しました', ErrorCodes.UNKNOWN_ERROR);
      }

      console.log('✅ 案件作成完了:', {
        id: data.id,
        vehicleModel: data.vehicle_model,
        vehiclePlateNumber: data.vehicle_plate_number,
      });

      // データベースの形式からCase型に変換
      return this.convertToCaseFormat(data);
    } catch (error) {
      console.error('❌ 案件作成エラー:', error);
      throw error instanceof DataServiceError
        ? error
        : new DataServiceError('案件の作成に失敗しました', ErrorCodes.UNKNOWN_ERROR);
    }
  }

  async updateCase(id: string, case_: Partial<Case>): Promise<Case> {
    try {
      if (!this.supabase) {
        throw new DataServiceError(
          'Supabaseへの接続が確立されていません',
          ErrorCodes.DATABASE_ERROR
        );
      }

      console.log('🔄 案件更新開始:', id);

      // 🔍 受け取った case_ パラメータの詳細分析（新規ログ）
      // ⚠️ 本番環境では無効化（PII保護）
      if (import.meta.env.MODE !== 'production') {
        console.log('[TRANSPORTER-DEBUG] 📋 updateCase受取データ詳細分析', {
          caseId: id,
          caseKeys: Object.keys(case_),
          hasAssignedLandTransporterId: 'assignedLandTransporterId' in case_,
          assignedLandTransporterId: case_.assignedLandTransporterId,
          assignedLandTransporterIdType: typeof case_.assignedLandTransporterId,
          isNull: case_.assignedLandTransporterId === null,
          isUndefined: case_.assignedLandTransporterId === undefined,
          // 注意: entireCaseを削除（個人情報保護のため）
        });
      }

      const updateData: any = {};

      // 更新するフィールドのみをセット
      if (case_.status !== undefined) updateData.status = case_.status;
      if ('assignedLandTransporterId' in case_) {
        // null値も明示的に処理（Supabaseでは空値はnullとして扱われる）
        updateData.assigned_transporter_id = case_.assignedLandTransporterId ?? null;
        console.log('[TRANSPORTER-DEBUG] 🔄 assigned_transporter_id更新', {
          original: case_.assignedLandTransporterId,
          converted: updateData.assigned_transporter_id,
          wasNull: case_.assignedLandTransporterId === null,
          wasUndefined: case_.assignedLandTransporterId === undefined,
        });
      } else {
        // 🚨 CRITICAL: assignedLandTransporterId が存在しない場合
        console.log('[TRANSPORTER-DEBUG] ⚠️ WARNING: assignedLandTransporterId is NOT in case_');
      }
      if (case_.billingPartnerId !== undefined)
        updateData.billing_partner_id = case_.billingPartnerId;
      if (case_.pickupPartnerId !== undefined) updateData.pickup_partner_id = case_.pickupPartnerId;
      if (case_.deliveryPartnerId !== undefined)
        updateData.delivery_partner_id = case_.deliveryPartnerId;
      if (case_.pickupAddress !== undefined) updateData.pickup_address_custom = case_.pickupAddress;
      if (case_.pickupPhone !== undefined) updateData.pickup_phone = case_.pickupPhone;
      if (case_.pickupDate !== undefined) updateData.pickup_date = case_.pickupDate;
      if (case_.pickupTimeStart !== undefined) updateData.pickup_time_start = case_.pickupTimeStart;
      if (case_.pickupTimeEnd !== undefined) updateData.pickup_time_end = case_.pickupTimeEnd;
      if (case_.deliveryAddress !== undefined)
        updateData.delivery_address_custom = case_.deliveryAddress;
      if (case_.deliveryPhone !== undefined) updateData.delivery_phone = case_.deliveryPhone;
      if (case_.deliveryDate !== undefined) updateData.delivery_date = case_.deliveryDate;
      if (case_.deliveryTime !== undefined) updateData.delivery_time = case_.deliveryTime;
      if (case_.vehiclePlateNumber !== undefined)
        updateData.vehicle_plate_number = case_.vehiclePlateNumber;
      if (case_.vehicleModel !== undefined) updateData.vehicle_model = case_.vehicleModel;
      if (case_.price !== undefined) updateData.price = case_.price;
      if (case_.remarks !== undefined) updateData.remarks = case_.remarks;
      if (case_.scheduledTime !== undefined) updateData.scheduled_time = case_.scheduledTime;

      // 更新日時を自動設定
      updateData.updated_at = new Date().toISOString();

      // 🔍 DB更新前の最終確認ログ
      console.log('[TRANSPORTER-DEBUG] 📤 DB更新前: updateData最終チェック', {
        caseId: id,
        updateDataKeys: Object.keys(updateData),
        hasAssignedTransporterId: 'assigned_transporter_id' in updateData,
        assignedTransporterId: updateData.assigned_transporter_id,
        entireUpdateData: updateData,
      });

      const { data, error } = await this.supabase
        .from('company_cases')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ 案件更新エラー:', error);
        console.error('[TRANSPORTER-DEBUG] ❌ DB更新エラー詳細', {
          caseId: id,
          error: error.message,
          errorDetails: error,
        });
        throw new DataServiceError(
          `案件の更新に失敗しました: ${error.message}`,
          ErrorCodes.DATABASE_ERROR
        );
      }

      if (!data) {
        throw new DataServiceError('案件の更新に失敗しました', ErrorCodes.UNKNOWN_ERROR);
      }

      // 🔍 DB更新後の結果確認ログ
      console.log('[TRANSPORTER-DEBUG] 📥 DB更新後: 返されたデータ確認', {
        caseId: id,
        returnedDataKeys: Object.keys(data),
        assigned_transporter_id: data.assigned_transporter_id,
        assignedLandTransporterId: data.assignedLandTransporterId,
        entireData: data,
      });

      console.log('✅ 案件更新完了:', id);

      // データベースの形式からCase型に変換
      const convertedCase = this.convertToCaseFormat(data);
      console.log('[TRANSPORTER-DEBUG] 🔄 変換後: Case型への変換確認', {
        caseId: id,
        convertedAssignedLandTransporterId: convertedCase.assignedLandTransporterId,
      });
      return convertedCase;
    } catch (error) {
      console.error('❌ 案件更新エラー:', error);
      throw error instanceof DataServiceError
        ? error
        : new DataServiceError('案件の更新に失敗しました', ErrorCodes.UNKNOWN_ERROR);
    }
  }

  async deleteCase(id: string): Promise<void> {
    try {
      if (!this.supabase) {
        throw new DataServiceError(
          'Supabaseへの接続が確立されていません',
          ErrorCodes.DATABASE_ERROR
        );
      }

      console.log('🔄 案件削除開始:', id);

      const { error } = await this.supabase.from('company_cases').delete().eq('id', id);

      if (error) {
        console.error('❌ 案件削除エラー:', error);
        throw new DataServiceError(
          `案件の削除に失敗しました: ${error.message}`,
          ErrorCodes.DATABASE_ERROR
        );
      }

      console.log('✅ 案件削除完了:', id);
    } catch (error) {
      console.error('❌ 案件削除エラー:', error);
      throw error instanceof DataServiceError
        ? error
        : new DataServiceError('案件の削除に失敗しました', ErrorCodes.UNKNOWN_ERROR);
    }
  }

  // データベースの形式からCase型に変換するヘルパーメソッド
  private convertToCaseFormat(row: any): Case {
    return {
      id: row.id,
      status: row.status || 'unassigned',

      // 陸送員
      assignedLandTransporterId: row.transporter_id || null,

      // 取引先
      billingPartnerId: row.billing_partner_id || null,
      pickupPartnerId: row.pickup_partner_id || null,
      deliveryPartnerId: row.delivery_partner_id || null,

      // 集荷
      pickupAddress: row.pickup_address_custom || '',
      pickupPhone: row.pickup_phone || '',
      pickupDate: row.pickup_date || '',
      pickupTimeStart: row.pickup_time_start || '',
      pickupTimeEnd: row.pickup_time_end || '',

      // 配送
      deliveryAddress: row.delivery_address_custom || '',
      deliveryPhone: row.delivery_phone || '',
      deliveryDate: row.delivery_date || '',
      deliveryTime: row.delivery_time || '',

      // 車両
      vehicleModel: row.vehicle_model || undefined,
      vehicleModelId: row.vehicle_model_id || undefined,
      vehicleType: row.vehicle_type || undefined,
      vehiclePlateNumber: row.vehicle_plate_number || undefined,
      vehiclePlateNumberNone: row.vehicle_plate_number_none || undefined,

      // FL-163: 車体番号 (acceptanceStatus removed - now using status)
      chassisNumber: row.chassis_number || undefined,

      // 請求
      price: row.price || 0,

      // その他
      remarks: row.remarks || '',

      // FL-113: 車検状態管理フィールド
      isVehicleInspectionExpired: row.is_vehicle_inspection_expired || false,
      requiresTemporaryNumber: row.requires_temporary_number || false,
      temporaryNumberNotes: row.temporary_number_notes || undefined,

      // FL-89: 新規フィールド（存在する場合）
      requester: row.requester || undefined,
      managementNumber: row.management_number || undefined,
      transportFee: row.transport_fee || undefined,

      // Legacy Client IDs (互換性のため)
      billingClientId: row.billing_partner_id || '',
      pickupClientId: row.pickup_partner_id || '',
      deliveryClientId: row.delivery_partner_id || '',

      // レポート（必須プロパティ）
      pickupReport: row.pickup_report || undefined,
      deliveryReport: row.delivery_report || undefined,
    };
  }

  // 車両モデル管理
  async getVehicleModels(): Promise<VehicleModel[]> {
    this.ensureSupabaseClient();

    try {
      console.log('[getVehicleModels] 開始');

      // 基本カラム（常に存在）
      const selectQuery = `
        id,
        name,
        manufacturer_id,
        vehicle_type_id,
        created_at,
        updated_at
      `;

      // 新しいカラムの存在チェック（エラーハンドリングで対応）
      let data, error;

      console.log('[getVehicleModels] 新しいスキーマでクエリ実行を試行');
      // まず新しいカラム構造でクエリ実行を試行
      const newSchemaResult = await this.supabase!.from('vehicle_models')
        .select(
          `
          id,
          name,
          manufacturer_id,
          vehicle_type_id,
          powertrain,
          vehicle_tags,
          data_source,
          created_at,
          updated_at
        `
        )
        .order('name');

      console.log('[getVehicleModels] 新しいスキーマクエリ結果:', {
        dataLength: newSchemaResult.data?.length,
        error: newSchemaResult.error,
      });

      // エラーが発生した場合（カラムが存在しない等）はフォールバック
      if (newSchemaResult.error && newSchemaResult.error.code === '42703') {
        console.warn(
          '[getVehicleModels] 新しいスキーマでの取得に失敗、旧スキーマで再試行:',
          newSchemaResult.error
        );

        // 旧カラム構造でフォールバック
        const fallbackResult = await this.supabase!.from('vehicle_models')
          .select(selectQuery)
          .order('name');

        data = fallbackResult.data;
        error = fallbackResult.error;
        console.log('[getVehicleModels] 旧スキーマクエリ結果:', {
          dataLength: data?.length,
          error,
        });
      } else {
        data = newSchemaResult.data;
        error = newSchemaResult.error;
      }

      if (error) {
        console.error('[getVehicleModels] Vehicle models取得エラー:', error);
        console.error('[getVehicleModels] エラー詳細:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log('[getVehicleModels] 成功 - データ件数:', data?.length || 0);

      return (data || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        manufacturerId: item.manufacturer_id,
        vehicleTypeId: item.vehicle_type_id,
        // 新しいカラムがない場合はデフォルト値を使用
        powertrain: item.powertrain || null,
        vehicleTags: item.vehicle_tags || [],
        dataSource: (item.data_source as VehicleModelDataSource) || 'custom',
      }));
    } catch (error) {
      console.error('VehicleModels取得エラー:', error);
      throw new DataServiceError(
        'Vehicle Models取得に失敗しました',
        ErrorCodes.DATA_FETCH_FAILED,
        error as Error
      );
    }
  }

  async createVehicleModel(model: Omit<VehicleModel, 'id'>): Promise<VehicleModel> {
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('vehicle_models')
        .insert({
          name: model.name,
          manufacturer_id: model.manufacturerId,
          vehicle_type_id: model.vehicleTypeId,
          powertrain: model.powertrain || null,
          vehicle_tags: model.vehicleTags || [],
          data_source: model.dataSource,
        })
        .select(
          `
          id,
          name,
          manufacturer_id,
          vehicle_type_id,
          powertrain,
          vehicle_tags,
          data_source,
          created_at,
          updated_at
        `
        )
        .single();

      if (error) {
        console.error('VehicleModel作成エラー:', error);
        throw error;
      }

      return {
        id: data.id,
        name: data.name,
        manufacturerId: data.manufacturer_id,
        vehicleTypeId: data.vehicle_type_id,
        powertrain: data.powertrain,
        vehicleTags: data.vehicle_tags || [],
        dataSource: data.data_source as VehicleModelDataSource,
      };
    } catch (error) {
      console.error('VehicleModel作成エラー:', error);
      throw new DataServiceError(
        'Vehicle Model作成に失敗しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  async updateVehicleModel(id: string, model: Partial<VehicleModel>): Promise<VehicleModel> {
    this.ensureSupabaseClient();

    try {
      const updateData: any = {};

      if (model.name !== undefined) updateData.name = model.name;
      if (model.manufacturerId !== undefined) updateData.manufacturer_id = model.manufacturerId;
      if (model.vehicleTypeId !== undefined) updateData.vehicle_type_id = model.vehicleTypeId;
      if (model.powertrain !== undefined) {
        updateData.powertrain = model.powertrain;
      }
      if ('vehicleTags' in model) updateData.vehicle_tags = model.vehicleTags || null;
      if (model.dataSource !== undefined) updateData.data_source = model.dataSource;

      const { data, error } = await this.supabase!.from('vehicle_models')
        .update(updateData)
        .eq('id', id)
        .select(
          `
          id,
          name,
          manufacturer_id,
          vehicle_type_id,
          powertrain,
          vehicle_tags,
          data_source,
          created_at,
          updated_at
        `
        )
        .single();

      if (error) {
        console.error('VehicleModel更新エラー:', error);
        throw error;
      }

      return {
        id: data.id,
        name: data.name,
        manufacturerId: data.manufacturer_id,
        vehicleTypeId: data.vehicle_type_id,
        powertrain: data.powertrain,
        vehicleTags: data.vehicle_tags || [],
        dataSource: data.data_source as VehicleModelDataSource,
      };
    } catch (error) {
      console.error('VehicleModel更新エラー:', error);
      throw new DataServiceError(
        'Vehicle Model更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }

  async deleteVehicleModel(id: string): Promise<void> {
    this.ensureSupabaseClient();

    try {
      // 削除前にデータソースを確認
      const { data: vehicleModel, error: fetchError } = await this.supabase!.from('vehicle_models')
        .select('data_source')
        .eq('id', id)
        .single();

      if (fetchError) {
        console.error('VehicleModel取得エラー:', fetchError);
        throw fetchError;
      }

      // ラクハイ提供データの削除を禁止
      if (vehicleModel?.data_source === VehicleModelDataSource.RAKUHAI_PROVIDED) {
        throw new DataServiceError(
          'ラクハイ提供の車両モデルは削除できません',
          ErrorCodes.DATA_DELETION_FAILED,
          new Error('Cannot delete rakuhai-provided vehicle model')
        );
      }

      const { error } = await this.supabase!.from('vehicle_models').delete().eq('id', id);

      if (error) {
        console.error('VehicleModel削除エラー:', error);
        throw error;
      }
    } catch (error) {
      console.error('VehicleModel削除エラー:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'Vehicle Model削除に失敗しました',
        ErrorCodes.DATA_DELETION_FAILED,
        error as Error
      );
    }
  }

  // 車種管理
  async getVehicleTypes(): Promise<VehicleType[]> {
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('vehicle_types')
        .select(
          `
          id,
          name,
          created_at,
          updated_at
        `
        )
        .order('name');

      if (error) {
        console.error('Vehicle types取得エラー:', error);
        throw error;
      }

      return (data || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
      }));
    } catch (error) {
      console.error('VehicleTypes取得エラー:', error);
      throw new DataServiceError(
        'Vehicle Types取得に失敗しました',
        ErrorCodes.DATA_FETCH_FAILED,
        error as Error
      );
    }
  }

  async createVehicleType(type: Omit<VehicleType, 'id'>): Promise<VehicleType> {
    this.ensureSupabaseClient();

    // SUPER_ADMIN権限チェック
    const currentUser = await this.getCurrentUser();
    if (!currentUser || currentUser.role !== Role.SUPER_ADMIN) {
      throw new DataServiceError(
        '車種の作成権限がありません。ラクハイ管理者のみ車種の作成が可能です。',
        ErrorCodes.PERMISSION_DENIED,
        new Error('Insufficient permissions for vehicle type creation')
      );
    }

    try {
      const { data, error } = await this.supabase!.from('vehicle_types')
        .insert({
          name: type.name,
        })
        .select(
          `
          id,
          name,
          created_at,
          updated_at
        `
        )
        .single();

      if (error) {
        console.error('VehicleType作成エラー:', error);
        throw error;
      }

      return {
        id: data.id,
        name: data.name,
      };
    } catch (error) {
      console.error('VehicleType作成エラー:', error);
      throw new DataServiceError(
        'Vehicle Type作成に失敗しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  async updateVehicleType(_id: string, _type: Partial<VehicleType>): Promise<VehicleType> {
    // SUPER_ADMIN権限チェック
    const currentUser = await this.getCurrentUser();
    if (!currentUser || currentUser.role !== Role.SUPER_ADMIN) {
      throw new DataServiceError(
        '車種の更新権限がありません。ラクハイ管理者のみ車種の更新が可能です。',
        ErrorCodes.PERMISSION_DENIED,
        new Error('Insufficient permissions for vehicle type update')
      );
    }

    throw new DataServiceError('Supabase車種機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  async deleteVehicleType(_id: string): Promise<void> {
    // SUPER_ADMIN権限チェック
    const currentUser = await this.getCurrentUser();
    if (!currentUser || currentUser.role !== Role.SUPER_ADMIN) {
      throw new DataServiceError(
        '車種の削除権限がありません。ラクハイ管理者のみ車種の削除が可能です。',
        ErrorCodes.PERMISSION_DENIED,
        new Error('Insufficient permissions for vehicle type deletion')
      );
    }

    throw new DataServiceError('Supabase車種機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  // Manufacturer management (missing methods)
  async getManufacturers(): Promise<Manufacturer[]> {
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('manufacturers')
        .select(
          `
          id,
          name,
          country,
          created_at,
          updated_at
        `
        )
        .order('name');

      if (error) {
        console.error('Manufacturers取得エラー:', error);
        throw error;
      }

      return (data || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        country: item.country || undefined,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
      }));
    } catch (error) {
      console.error('Manufacturers取得エラー:', error);
      throw new DataServiceError(
        'Manufacturers取得に失敗しました',
        ErrorCodes.DATA_FETCH_FAILED,
        error as Error
      );
    }
  }

  async createManufacturer(_manufacturer: Omit<Manufacturer, 'id'>): Promise<Manufacturer> {
    throw new DataServiceError('Supabase車両メーカー機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  async updateManufacturer(
    _id: string,
    _manufacturer: Partial<Manufacturer>
  ): Promise<Manufacturer> {
    throw new DataServiceError('Supabase車両メーカー機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  async deleteManufacturer(_id: string): Promise<void> {
    throw new DataServiceError('Supabase車両メーカー機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  // Vehicle management methods removed - no longer supported

  // BusinessPartner management (missing methods)

  private async getBusinessPartnersStub(): Promise<BusinessPartner[]> {
    // スタブ実装 - ラクハイ提供の52件のデータを使用
    console.log('Supabase未接続のため、ラクハイ提供データ（52件）のスタブ実装を使用します');

    // newBusinessPartnersをインポートして使用
    const { newBusinessPartners } = await import('./newBusinessPartners');
    return newBusinessPartners;
  }

  async createBusinessPartner(partnerData: Omit<BusinessPartner, 'id'>): Promise<BusinessPartner> {
    console.log('🚀 createBusinessPartner開始:', partnerData.name);

    if (!isSupabaseAvailable() || !this.supabase) {
      console.error('❌ Supabase接続失敗 - デバッグ情報:');
      debugSupabaseConnection();
      throw new DataServiceError(
        'Supabaseへの接続が利用できません。システム管理者にお問い合わせください。',
        ErrorCodes.NETWORK_ERROR
      );
    }

    // デモモード時はService Role認証でRLSをバイパス
    const isDemoMode = this.isDemo;
    let supabaseClient = this.supabase;

    if (isDemoMode) {
      console.log('🎭 デモモード: Service Role認証を使用してRLSをバイパスします');

      // Service Role キーが設定されている場合のみ使用
      const serviceRoleKey = import.meta.env?.VITE_SUPABASE_SERVICE_ROLE_KEY;

      if (serviceRoleKey) {
        console.log('🔑 Service Role認証を使用');
        const { createClient } = await import('@supabase/supabase-js');
        supabaseClient = createClient(import.meta.env.VITE_SUPABASE_URL || '', serviceRoleKey, {
          auth: {
            autoRefreshToken: false,
            persistSession: false,
          },
        });
      } else {
        console.warn('⚠️ Service Role キーが設定されていません。通常の認証を使用します。');

        // Service Roleキーがない場合は、company_idを強制的に設定
        console.log('🎭 デモモード: company_idを強制設定します');
        partnerData = {
          ...partnerData,
          // デモ会社IDを強制設定（RLSポリシーが適切に動作するように）
        };
      }
    }

    try {
      // business_partnersテーブルに挿入（適切なクライアントを使用）
      const { data: partner, error: partnerError } = await supabaseClient
        .from('business_partners')
        .insert([
          {
            name: partnerData.name,
            display_name: partnerData.displayName,
            type: partnerData.type,
            status: partnerData.status,
            data_source: partnerData.dataSource,
            category: partnerData.category,
            parent_id: partnerData.parentId,
            available_roles: partnerData.availableRoles,
            default_address: partnerData.defaultAddress,
            default_phone: partnerData.defaultPhone,
            default_contact_person: partnerData.defaultContactPerson,
            default_email: partnerData.defaultEmail,
            notes: partnerData.notes,
            is_system_preset: partnerData.isSystemPreset,
            // 🔑 重要: company_idを必ず設定（RLS権限のため）
            company_id: isDemoMode ? this.DEMO_COMPANY_ID : this.getCurrentCompanyId(),
          },
        ])
        .select()
        .single();

      if (partnerError) {
        throw new DataServiceError(
          `取引先の作成に失敗しました: ${partnerError.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          partnerError
        );
      }

      // 拠点情報も挿入
      if (partnerData.locations && partnerData.locations.length > 0) {
        const companyId = isDemoMode ? this.DEMO_COMPANY_ID : this.getCurrentCompanyId();
        const locationsData = partnerData.locations.map(loc => ({
          business_partner_id: partner.id,
          name: loc.name,
          address: loc.address,
          phone: loc.phone,
          contact_person: loc.contactPerson,
          email: loc.email,
          is_default: loc.isDefault,
          roles: loc.roles,
          // 🔑 重要: company_idを必ず設定（RLS権限のため）
          company_id: companyId,
        }));

        const { error: locationsError } = await supabaseClient
          .from('business_partner_locations')
          .insert(locationsData);

        if (locationsError) {
          console.error('拠点情報の作成に失敗しました:', locationsError);
        }
      }

      // 🆕 company_partner_selectionsに自動登録（取引先を有効化するため）
      const companyId = isDemoMode ? this.DEMO_COMPANY_ID : this.getCurrentCompanyId();
      if (companyId) {
        try {
          console.log('📝 取引先をcompany_partner_selectionsに自動登録します');
          await this.addCompanyPartnerSelection({
            companyId: companyId,
            businessPartnerId: partner.id,
            isEnabled: true,
            roles: [BusinessPartnerRole.PICKUP, BusinessPartnerRole.DELIVERY],
            priority: 0,
            billingAllowed: true,
            pickupAllowed: true,
            deliveryAllowed: true,
            usageCount: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
          console.log('✅ company_partner_selectionsへの自動登録が完了しました');
        } catch (selectionError) {
          // company_partner_selections登録が失敗しても取引先作成自体は成功扱い
          console.error(
            '⚠️ company_partner_selections登録は失敗しましたが、取引先作成は成功しました:',
            selectionError
          );
        }
      }

      // 可能ならDBから返却された最新データをマッピングして返す（再取得に依存しない）
      const mapped: BusinessPartner = {
        id: partner.id,
        name: partner.name,
        displayName: partner.display_name ?? '',
        type: partner.type ?? undefined,
        status: partner.status,
        dataSource: partner.data_source,
        category: partner.category,
        parentId: partner.parent_id ?? null,
        availableRoles: partner.available_roles ?? [],
        defaultAddress: partner.default_address ?? null,
        defaultPhone: partner.default_phone ?? null,
        defaultContactPerson: partner.default_contact_person ?? null,
        defaultEmail: partner.default_email ?? null,
        notes: partner.notes ?? null,
        isSystemPreset: !!partner.is_system_preset,
        companyId: partner.company_id ?? undefined,
        createdAt: partner.created_at ?? new Date().toISOString(),
        updatedAt: partner.updated_at ?? new Date().toISOString(),
        locations: partnerData.locations || [],
      } as BusinessPartner;

      return mapped;
    } catch (error) {
      console.error('❌ createBusinessPartner実行エラー詳細:', error);

      // エラーの詳細情報をログに出力
      if (error && typeof error === 'object') {
        console.error('- エラーコード:', (error as any).code);
        console.error('- エラーメッセージ:', (error as any).message);
        console.error('- エラー詳細:', (error as any).details);
        console.error('- ヒント:', (error as any).hint);
      }

      // 認証エラーの場合
      if (error && (error as any).code === '401') {
        throw new DataServiceError(
          'Supabaseへの認証が必要です。ログインしてください。',
          ErrorCodes.AUTHENTICATION_ERROR
        );
      }

      // RLS権限エラーの場合
      if (
        error &&
        ((error as any).code === 'PGRST301' || (error as any).message?.includes('RLS'))
      ) {
        throw new DataServiceError(
          'データベースへの書き込み権限がありません。管理者にお問い合わせください。',
          ErrorCodes.PERMISSION_DENIED
        );
      }

      // その他のエラーも適切に投げる（スタブにフォールバックしない）
      throw new DataServiceError(
        `取引先の作成に失敗しました: ${(error as any).message || 'Unknown error'}`,
        ErrorCodes.DATABASE_ERROR
      );
    }
  }

  async updateBusinessPartner(
    id: string,
    partnerData: Partial<BusinessPartner>
  ): Promise<BusinessPartner> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabase未設定のため、スタブ実装を使用します');
      return this.updateBusinessPartnerStub(id, partnerData);
    }

    try {
      const { data, error } = await this.supabase
        .from('business_partners')
        .update({
          name: partnerData.name,
          display_name: partnerData.displayName,
          type: partnerData.type,
          status: partnerData.status,
          data_source: partnerData.dataSource,
          category: partnerData.category,
          parent_id: partnerData.parentId,
          available_roles: partnerData.availableRoles,
          default_address: partnerData.defaultAddress,
          default_phone: partnerData.defaultPhone,
          default_contact_person: partnerData.defaultContactPerson,
          default_email: partnerData.defaultEmail,
          notes: partnerData.notes,
          is_system_preset: partnerData.isSystemPreset,
          company_id: partnerData.companyId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          `取引先の更新に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }

      // 可能ならDBから返却された最新データをマッピングして返す
      if (data) {
        const mapped: BusinessPartner = {
          id: data.id,
          name: data.name,
          displayName: data.display_name ?? '',
          type: data.type ?? undefined,
          status: data.status,
          dataSource: data.data_source,
          category: data.category,
          parentId: data.parent_id ?? null,
          availableRoles: data.available_roles ?? [],
          defaultAddress: data.default_address ?? null,
          defaultPhone: data.default_phone ?? null,
          defaultContactPerson: data.default_contact_person ?? null,
          defaultEmail: data.default_email ?? null,
          notes: data.notes ?? null,
          isSystemPreset: !!data.is_system_preset,
          companyId: data.company_id ?? undefined,
          createdAt: data.created_at ?? new Date().toISOString(),
          updatedAt: data.updated_at ?? new Date().toISOString(),
          locations: Array.isArray(data.business_partner_locations)
            ? data.business_partner_locations
            : [],
        } as BusinessPartner;
        return mapped;
      }

      // dataが返らない場合は最小データを合成して返す（再取得に依存しない）
      return {
        id,
        name: partnerData.name ?? '',
        displayName: partnerData.displayName ?? '',
        type: partnerData.type ?? undefined,
        status: partnerData.status ?? undefined,
        dataSource: partnerData.dataSource ?? undefined,
        category: partnerData.category ?? undefined,
        parentId: partnerData.parentId ?? null,
        availableRoles: partnerData.availableRoles ?? [],
        defaultAddress: partnerData.defaultAddress ?? null,
        defaultPhone: partnerData.defaultPhone ?? null,
        defaultContactPerson: partnerData.defaultContactPerson ?? null,
        defaultEmail: partnerData.defaultEmail ?? null,
        notes: partnerData.notes ?? null,
        isSystemPreset: partnerData.isSystemPreset ?? false,
        companyId: partnerData.companyId ?? undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        locations: [],
      } as BusinessPartner;
    } catch (error) {
      console.error('Supabaseでの更新に失敗:', error);
      // 既知のアプリケーションエラーはそのまま伝播
      if (error instanceof DataServiceError) {
        throw error;
      }
      // ネットワークなどで例外が発生した場合はスタブにフォールバック
      return this.updateBusinessPartnerStub(id, partnerData);
    }
  }

  private async updateBusinessPartnerStub(
    id: string,
    partnerData: Partial<BusinessPartner>
  ): Promise<BusinessPartner> {
    // スタブ実装 - 既存データを模擬的に更新
    const existingPartners = await this.getBusinessPartnersStub();
    const existing = existingPartners.find(p => p.id === id);

    if (!existing) {
      throw new DataServiceError('取引先が見つかりません', ErrorCodes.UNKNOWN_ERROR);
    }

    return {
      ...existing,
      ...partnerData,
      id, // IDは変更しない
      updatedAt: new Date().toISOString(),
    };
  }

  async deleteBusinessPartner(id: string): Promise<void> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabase未設定のため、スタブ実装を使用します');
      return this.deleteBusinessPartnerStub(id);
    }

    try {
      // 関連する拠点情報も削除される（CASCADE設定による）
      const { error } = await this.supabase.from('business_partners').delete().eq('id', id);

      if (error) {
        throw new DataServiceError(
          `取引先の削除に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }
    } catch (error) {
      console.error('Supabaseでの削除に失敗しました。スタブ実装を使用します:', error);
      return this.deleteBusinessPartnerStub(id);
    }
  }

  private async deleteBusinessPartnerStub(_id: string): Promise<void> {
    // スタブ実装 - 削除処理の模擬（実際は何もしない）
    console.log('スタブ実装: 取引先削除処理完了');
  }

  async addBusinessPartnerLocation(
    _partnerId: string,
    _location: Omit<BusinessPartnerLocation, 'id'>
  ): Promise<BusinessPartner> {
    throw new DataServiceError('Supabase取引先機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  async updateBusinessPartnerLocation(
    _partnerId: string,
    _locationId: string,
    _location: Partial<BusinessPartnerLocation>
  ): Promise<BusinessPartner> {
    throw new DataServiceError('Supabase取引先機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  async deleteBusinessPartnerLocation(
    _partnerId: string,
    _locationId: string
  ): Promise<BusinessPartner> {
    throw new DataServiceError('Supabase取引先機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  async getClientsFromBusinessPartners(): Promise<Client[]> {
    // DEPRECATED: BusinessPartner統合済み - MasterDataContextのgetClientByIdを使用
    console.warn(
      '[DEPRECATED] getClientsFromBusinessPartners() - Use MasterDataContext.getClientById() instead'
    );
    return [];
  }

  async createClientFromBusinessPartner(_clientData: Omit<Client, 'id'>): Promise<Client> {
    throw new DataServiceError('Supabase取引先機能は実装中です', ErrorCodes.UNKNOWN_ERROR);
  }

  // ========================================
  // CompanyPartnerSelection関連のメソッド
  // ========================================

  async getCompanyPartnerSelectionsByCompanyId(
    companyId: string
  ): Promise<CompanyPartnerSelection[]> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabase未設定のため、空配列を返します');
      return [];
    }

    try {
      const { data, error } = await this.supabase
        .from('company_partner_selections')
        .select('*')
        .eq('company_id', companyId);

      if (error) {
        throw new DataServiceError(
          `会社別取引先選択設定の取得に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }

      return (data || []).map((selection: any) => ({
        id: selection.id,
        companyId: selection.company_id,
        businessPartnerId: selection.business_partner_id,
        isEnabled: selection.is_enabled,
        roles: selection.roles || [],
        priority: selection.priority || 0,
        customName: selection.custom_name || undefined,
        customNotes: selection.custom_notes || undefined,
        billingAllowed: selection.billing_allowed,
        pickupAllowed: selection.pickup_allowed,
        deliveryAllowed: selection.delivery_allowed,
        usageCount: selection.usage_count || 0,
        lastUsedAt: selection.last_used_at || undefined,
        createdAt: selection.created_at,
        updatedAt: selection.updated_at,
        createdBy: selection.created_by || undefined,
      }));
    } catch (error) {
      console.error('CompanyPartnerSelections取得エラー:', error);
      return [];
    }
  }

  async addCompanyPartnerSelection(selection: Omit<CompanyPartnerSelection, 'id'>): Promise<void> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabase未設定のため、処理をスキップします');
      return;
    }

    try {
      const { error } = await this.supabase.from('company_partner_selections').insert({
        company_id: selection.companyId,
        business_partner_id: selection.businessPartnerId,
        is_enabled: selection.isEnabled,
        roles: selection.roles,
        priority: selection.priority,
        custom_name: selection.customName || null,
        custom_notes: selection.customNotes || null,
        billing_allowed: selection.billingAllowed,
        pickup_allowed: selection.pickupAllowed,
        delivery_allowed: selection.deliveryAllowed,
        usage_count: selection.usageCount || 0,
        created_by: selection.createdBy || null,
      });

      if (error) {
        throw new DataServiceError(
          `会社別取引先選択設定の作成に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }
    } catch (error) {
      console.error('CompanyPartnerSelection作成エラー:', error);
      throw new DataServiceError(
        '会社別取引先選択設定の作成に失敗しました',
        ErrorCodes.UNKNOWN_ERROR,
        error as Error
      );
    }
  }

  async updateCompanyPartnerSelection(
    id: string,
    selection: Partial<CompanyPartnerSelection>
  ): Promise<void> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabase未設定のため、処理をスキップします');
      return;
    }

    try {
      const updateData: any = {};

      if (selection.isEnabled !== undefined) updateData.is_enabled = selection.isEnabled;
      if (selection.roles !== undefined) updateData.roles = selection.roles;
      if (selection.priority !== undefined) updateData.priority = selection.priority;
      if (selection.customName !== undefined) updateData.custom_name = selection.customName;
      if (selection.customNotes !== undefined) updateData.custom_notes = selection.customNotes;
      if (selection.billingAllowed !== undefined)
        updateData.billing_allowed = selection.billingAllowed;
      if (selection.pickupAllowed !== undefined)
        updateData.pickup_allowed = selection.pickupAllowed;
      if (selection.deliveryAllowed !== undefined)
        updateData.delivery_allowed = selection.deliveryAllowed;
      if (selection.usageCount !== undefined) updateData.usage_count = selection.usageCount;
      if (selection.lastUsedAt !== undefined) updateData.last_used_at = selection.lastUsedAt;

      const { error } = await this.supabase
        .from('company_partner_selections')
        .update(updateData)
        .eq('id', id);

      if (error) {
        throw new DataServiceError(
          `会社別取引先選択設定の更新に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }
    } catch (error) {
      console.error('CompanyPartnerSelection更新エラー:', error);
      throw new DataServiceError(
        '会社別取引先選択設定の更新に失敗しました',
        ErrorCodes.UNKNOWN_ERROR,
        error as Error
      );
    }
  }

  async removeCompanyPartnerSelection(id: string): Promise<void> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabase未設定のため、処理をスキップします');
      return;
    }

    try {
      const { error } = await this.supabase
        .from('company_partner_selections')
        .delete()
        .eq('id', id);

      if (error) {
        throw new DataServiceError(
          `会社別取引先選択設定の削除に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }
    } catch (error) {
      console.error('CompanyPartnerSelection削除エラー:', error);
      throw new DataServiceError(
        '会社別取引先選択設定の削除に失敗しました',
        ErrorCodes.UNKNOWN_ERROR,
        error as Error
      );
    }
  }

  async enableCompanyPartnerSelection(
    companyId: string,
    businessPartnerId: string,
    roles: BusinessPartnerRole[]
  ): Promise<void> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabase未設定のため、処理をスキップします');
      return;
    }

    try {
      // 既存の設定をチェック
      const { data: existing } = await this.supabase
        .from('company_partner_selections')
        .select('id')
        .eq('company_id', companyId)
        .eq('business_partner_id', businessPartnerId)
        .single();

      if (existing) {
        // 既存の設定を更新
        await this.updateCompanyPartnerSelection(existing.id, {
          isEnabled: true,
          roles: roles,
          billingAllowed: roles.includes(BusinessPartnerRole.BILLING),
          pickupAllowed: roles.includes(BusinessPartnerRole.PICKUP),
          deliveryAllowed: roles.includes(BusinessPartnerRole.DELIVERY),
        });
      } else {
        // 新しい設定を作成
        await this.addCompanyPartnerSelection({
          companyId,
          businessPartnerId,
          isEnabled: true,
          roles: roles,
          priority: 0,
          billingAllowed: roles.includes(BusinessPartnerRole.BILLING),
          pickupAllowed: roles.includes(BusinessPartnerRole.PICKUP),
          deliveryAllowed: roles.includes(BusinessPartnerRole.DELIVERY),
          usageCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('CompanyPartnerSelection有効化エラー:', error);
      throw new DataServiceError(
        '会社別取引先選択設定の有効化に失敗しました',
        ErrorCodes.UNKNOWN_ERROR,
        error as Error
      );
    }
  }

  async disableCompanyPartnerSelection(
    companyId: string,
    businessPartnerId: string
  ): Promise<void> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabase未設定のため、処理をスキップします');
      return;
    }

    try {
      const { error } = await this.supabase
        .from('company_partner_selections')
        .update({ is_enabled: false })
        .eq('company_id', companyId)
        .eq('business_partner_id', businessPartnerId);

      if (error) {
        throw new DataServiceError(
          `会社別取引先選択設定の無効化に失敗しました: ${error.message}`,
          ErrorCodes.UNKNOWN_ERROR,
          error
        );
      }
    } catch (error) {
      console.error('CompanyPartnerSelection無効化エラー:', error);
      throw new DataServiceError(
        '会社別取引先選択設定の無効化に失敗しました',
        ErrorCodes.UNKNOWN_ERROR,
        error as Error
      );
    }
  }

  async updateCompanyPartnerUsage(
    companyId: string,
    businessPartnerId: string,
    caseId?: string,
    role?: BusinessPartnerRole
  ): Promise<void> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabase未設定のため、処理をスキップします');
      return;
    }

    try {
      // 使用ログを記録
      await this.supabase.from('company_partner_usage_logs').insert({
        company_id: companyId,
        business_partner_id: businessPartnerId,
        case_id: caseId || null,
        role: role || null,
      });

      // 使用回数を更新
      const { data: selection } = await this.supabase
        .from('company_partner_selections')
        .select('id, usage_count')
        .eq('company_id', companyId)
        .eq('business_partner_id', businessPartnerId)
        .single();

      if (selection) {
        await this.updateCompanyPartnerSelection(selection.id, {
          usageCount: (selection.usage_count || 0) + 1,
          lastUsedAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.warn('CompanyPartnerSelection使用実績記録エラー:', error);
      // 使用実績の記録エラーは警告のみで処理続行
    }
  }

  // CompanyPartnerSettings CRUD operations
  async getCompanyPartnerSettings(companyId: string): Promise<CompanyPartnerSettings | null> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabaseが利用できません。デフォルト設定を返します。');
      return null;
    }

    try {
      const result = await this.supabase
        .from('company_partner_settings')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (!result) {
        console.error('[SupabaseService] Supabaseからの応答がありません。');
        return null;
      }

      const { data, error } = result;

      if (error && error.code !== 'PGRST116') {
        console.error('[SupabaseService] CompanyPartnerSettings取得エラー:', {
          error,
          companyId,
          errorCode: error.code,
          errorMessage: error.message,
        });
        return null;
      }

      if (!data) {
        return null;
      }

      return {
        id: data.id,
        companyId: data.company_id,
        enabledPartnerIds: data.enabled_partner_ids || [],
        dataSourcePreferences: {
          allowRakuhaiProvided: true,
          allowCustom: true,
        },
        categoryPreferences: {
          allowDealership: true,
          allowAuction: true,
          allowOther: true,
        },
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error) {
      console.error('CompanyPartnerSettings取得エラー:', error);
      return null;
    }
  }

  async createCompanyPartnerSettings(
    settings: Omit<CompanyPartnerSettings, 'id'>
  ): Promise<CompanyPartnerSettings> {
    if (!isSupabaseAvailable() || !this.supabase) {
      const mockSettings: CompanyPartnerSettings = {
        id: 'mock-' + Date.now(),
        ...settings,
      };
      console.warn('Supabaseが利用できないため、CompanyPartnerSettings作成をスキップします');
      return mockSettings;
    }

    try {
      const { data, error } = await this.supabase
        .from('company_partner_settings')
        .upsert(
          {
            company_id: settings.companyId,
            enabled_partner_ids: settings.enabledPartnerIds,
            auto_enable_new_partners: false,
            partner_selection_mode: 'manual',
          },
          {
            onConflict: 'company_id',
          }
        )
        .select()
        .single();

      if (error) {
        console.error('[SupabaseService] CompanyPartnerSettings作成エラー:', {
          error,
          settings,
          errorCode: error.code,
          errorMessage: error.message,
          supabaseInitialized: !!this.supabase,
        });
        throw error;
      }

      // データベースからの戻り値をCompanyPartnerSettings型に変換
      return {
        id: data.id,
        companyId: data.company_id,
        enabledPartnerIds: data.enabled_partner_ids || [],
        dataSourcePreferences: {
          allowRakuhaiProvided: true,
          allowCustom: true,
        },
        categoryPreferences: {
          allowDealership: true,
          allowAuction: true,
          allowOther: true,
        },
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error) {
      console.error('[SupabaseService] CompanyPartnerSettings作成エラー (catch):', {
        error,
        settings,
        supabaseInitialized: !!this.supabase,
      });
      throw error;
    }
  }

  async updateCompanyPartnerSettings(
    id: string,
    settings: Partial<CompanyPartnerSettings>
  ): Promise<CompanyPartnerSettings> {
    if (!isSupabaseAvailable() || !this.supabase) {
      // モック用に現在の設定を取得して更新したものを返す
      const mockSettings: CompanyPartnerSettings = {
        id: id,
        companyId: settings.companyId || 'mock-company',
        enabledPartnerIds: settings.enabledPartnerIds || [],
        dataSourcePreferences: settings.dataSourcePreferences || {
          allowRakuhaiProvided: true,
          allowCustom: true,
        },
        categoryPreferences: settings.categoryPreferences || {
          allowDealership: true,
          allowAuction: true,
          allowOther: true,
        },
        createdAt: settings.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      console.warn('Supabaseが利用できないため、CompanyPartnerSettings更新をスキップします');
      return mockSettings;
    }

    try {
      const updateData: any = {};

      if (settings.enabledPartnerIds !== undefined) {
        updateData.enabled_partner_ids = settings.enabledPartnerIds;
      }
      // dataSourcePreferences と categoryPreferences はテーブルに存在しないため削除

      updateData.updated_at = new Date().toISOString();

      const { data, error } = await this.supabase
        .from('company_partner_settings')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('CompanyPartnerSettings更新エラー:', error);
        throw error;
      }

      // データベースからの戻り値をCompanyPartnerSettings型に変換
      return {
        id: data.id,
        companyId: data.company_id,
        enabledPartnerIds: data.enabled_partner_ids || [],
        dataSourcePreferences: {
          allowRakuhaiProvided: true,
          allowCustom: true,
        },
        categoryPreferences: {
          allowDealership: true,
          allowAuction: true,
          allowOther: true,
        },
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error) {
      console.error('CompanyPartnerSettings更新エラー:', error);
      throw error;
    }
  }

  async deleteCompanyPartnerSettings(companyId: string): Promise<void> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabaseが利用できないため、CompanyPartnerSettings削除をスキップします');
      return;
    }

    try {
      const { error } = await this.supabase
        .from('company_partner_settings')
        .delete()
        .eq('company_id', companyId);

      if (error) {
        console.error('CompanyPartnerSettings削除エラー:', error);
        throw error;
      }
    } catch (error) {
      console.error('CompanyPartnerSettings削除エラー:', error);
      throw error;
    }
  }

  // データリセット
  async resetData(): Promise<void> {
    console.log('[SupabaseMasterDataService] データリセットはSupabaseモードでは無効です');
    console.warn(
      '本番環境ではデータリセット機能は利用できません。開発・テスト目的の場合はモックモードを使用してください。'
    );

    // 本番環境では安全のためリセット機能を無効化
    throw new DataServiceError(
      'データリセット機能は本番環境では利用できません。開発・テスト用途ではモックモードをご利用ください。',
      ErrorCodes.PERMISSION_DENIED
    );
  }

  /**
   * デモモード専用のデータリセット機能
   * 既存のシードデータを使用して初期状態に復元する
   */
  async resetDemoData(): Promise<void> {
    console.log('[SupabaseMasterDataService] デモデータリセット開始');

    // デモモードでない場合はエラー
    if (!isDemoMode()) {
      throw new DataServiceError(
        'デモデータリセット機能はデモモードでのみ利用可能です。',
        ErrorCodes.PERMISSION_DENIED
      );
    }

    this.ensureSupabaseClient();

    try {
      console.log('🔄 デモデータを初期状態に復元中...');

      // 現在の会社IDを取得
      const currentUser = await this.getCurrentUser();
      const companyId = currentUser?.companyId || this.currentCompanyId;

      if (!companyId) {
        throw new DataServiceError(
          '会社IDが見つかりません。ログインし直してください。',
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }

      // 1. 既存データのクリア（会社別分離を維持）
      await this.clearDemoData(companyId);

      // 2. シードデータからの初期データ復元
      await this.restoreInitialData(companyId);

      // 3. デモシナリオ用追加データの投入
      await this.insertDemoScenarioData(companyId);

      // 4. フォールバックデータをローカルストレージに確実に反映
      this.refreshLocalFallbackData();

      console.log('✅ デモデータリセット完了');
    } catch (error) {
      console.error('❌ デモデータリセット失敗:', error);
      throw new DataServiceError(
        `デモデータリセットに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
        ErrorCodes.UNKNOWN_ERROR
      );
    }
  }

  /**
   * デモデータのクリア（会社別）
   */
  private async clearDemoData(companyId: string): Promise<void> {
    const supabase = this.ensureSupabaseClient();
    console.log('🗑️ 既存デモデータをクリア中...');

    // 関連データを適切な順序で削除（外部キー制約を考慮）
    const clearQueries = [
      // 案件関連（依存関係の最上位）
      'DELETE FROM company_cases WHERE company_id = $1',

      // 取引先選択設定
      'DELETE FROM company_partner_selections WHERE company_id = $1',
      'DELETE FROM company_partner_settings WHERE company_id = $1',

      // 時間・OCR設定
      'DELETE FROM company_time_settings WHERE company_id = $1',
      'DELETE FROM company_ocr_settings WHERE company_id = $1',

      // カスタムマスターデータ（会社固有データのみ）
      "DELETE FROM business_partners WHERE company_id = $1 AND data_source = 'CUSTOM'",
      'DELETE FROM vehicles WHERE company_id = $1',
      'DELETE FROM land_transporters WHERE company_id = $1',
      'DELETE FROM clients WHERE company_id = $1',
    ];

    for (const query of clearQueries) {
      try {
        await supabase.rpc('execute_sql', {
          query,
          params: [companyId],
        });
      } catch (error) {
        console.warn(`クリアクエリ実行中の警告: ${query}`, error);
        // 個別のクリアエラーは警告として扱い、処理を継続
      }
    }
  }

  /**
   * 初期データの復元
   */
  private async restoreInitialData(companyId: string): Promise<void> {
    console.log('📋 初期マスターデータを復元中...');

    try {
      // デフォルト会社設定の作成
      await this.createDefaultCompanySettings(companyId);

      // ラクハイ提供データは既存のSeedデータを活用（company_id無しでグローバル）
      // カスタムデータのサンプルを少量追加
      await this.createSampleCustomData(companyId);
    } catch (error) {
      console.error('初期データ復元エラー:', error);
      throw error;
    }
  }

  /**
   * デフォルト会社設定の作成
   */
  private async createDefaultCompanySettings(companyId: string): Promise<void> {
    const supabase = this.ensureSupabaseClient();
    // 時間設定のデフォルト作成
    const { error: timeError } = await supabase.from('company_time_settings').insert({
      company_id: companyId,
      lunch_break_start: '12:00',
      lunch_break_end: '13:00',
      short_distance_time: 30,
      medium_distance_time: 60,
      long_distance_time: 120,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });

    if (timeError) {
      console.warn('時間設定作成警告:', timeError);
    }

    // OCR設定のデフォルト作成
    const { error: ocrError } = await supabase.from('company_ocr_settings').insert({
      company_id: companyId,
      enabled: true,
      confidence_threshold: 0.8,
      auto_extract_fields: ['pickup_address', 'delivery_address'],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });

    if (ocrError) {
      console.warn('OCR設定作成警告:', ocrError);
    }
  }

  /**
   * サンプルカスタムデータの作成
   */
  private async createSampleCustomData(companyId: string): Promise<void> {
    const supabase = this.ensureSupabaseClient();
    // サンプル顧客データ
    const sampleClients = [
      {
        company_id: companyId,
        name: '田中自動車販売',
        email: 'tanaka@sample.com',
        phone: '03-1234-5678',
        address: '東京都新宿区西新宿1-1-1',
        status: 'Active' as const,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        company_id: companyId,
        name: '山田モータース',
        email: 'yamada@sample.com',
        phone: '06-9876-5432',
        address: '大阪府大阪市北区梅田1-1-1',
        status: 'Active' as const,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ];

    const { error: clientError } = await supabase.from('clients').insert(sampleClients);

    if (clientError) {
      console.warn('サンプル顧客作成警告:', clientError);
    }

    // サンプル陸送員データ
    const sampleDrivers = [
      {
        company_id: companyId,
        name: '佐藤 太郎',
        license_number: 'T123456789',
        phone: '090-1111-2222',
        email: 'sato@demo.com',
        status: 'Available' as const,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        company_id: companyId,
        name: '鈴木 次郎',
        license_number: 'T987654321',
        phone: '090-3333-4444',
        email: 'suzuki@demo.com',
        status: 'Available' as const,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ];

    const { error: driverError } = await supabase
      .from('company_land_transporters')
      .insert(sampleDrivers);

    if (driverError) {
      console.warn('サンプル陸送員作成警告:', driverError);
    }
  }

  /**
   * デモシナリオ用データの投入
   */
  private async insertDemoScenarioData(companyId: string): Promise<void> {
    const supabase = this.ensureSupabaseClient();
    console.log('🎭 デモシナリオ用データを投入中...');

    // 現在日時を基準とした日程設定
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    // サンプル案件データ（様々なステータス・パターン）
    const demoClient = await supabase
      .from('clients')
      .select('id')
      .eq('company_id', companyId)
      .eq('name', '田中自動車販売')
      .single();

    const demoDriver = await supabase
      .from('company_land_transporters')
      .select('id')
      .eq('company_id', companyId)
      .eq('name', '佐藤 太郎')
      .single();

    if (demoClient?.data && demoDriver?.data) {
      const scenarioCases = [
        {
          company_id: companyId,
          client_id: demoClient.data.id,
          pickup_address: '東京都港区六本木1-1-1',
          delivery_address: '神奈川県横浜市西区みなとみらい2-2-2',
          pickup_date: today.toISOString().split('T')[0],
          pickup_time: '09:00',
          delivery_date: today.toISOString().split('T')[0],
          delivery_time: '14:00',
          vehicle_type: '小型車',
          transport_fee: 25000,
          status: 'Assigned' as const,
          assigned_transporter_id: demoDriver.data.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          company_id: companyId,
          client_id: demoClient.data.id,
          pickup_address: '大阪府大阪市中央区難波1-1-1',
          delivery_address: '兵庫県神戸市中央区三宮2-2-2',
          pickup_date: tomorrow.toISOString().split('T')[0],
          pickup_time: '10:30',
          delivery_date: tomorrow.toISOString().split('T')[0],
          delivery_time: '15:30',
          vehicle_type: '普通車',
          transport_fee: 35000,
          status: 'unassigned' as const,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          company_id: companyId,
          client_id: demoClient.data.id,
          pickup_address: '愛知県名古屋市中区栄3-3-3',
          delivery_address: '静岡県浜松市中区鍛冶町4-4-4',
          pickup_date: nextWeek.toISOString().split('T')[0],
          pickup_time: '08:00',
          delivery_date: nextWeek.toISOString().split('T')[0],
          delivery_time: '13:00',
          vehicle_type: '大型車',
          transport_fee: 45000,
          status: 'unassigned' as const,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ];

      const { error: caseError } = await supabase.from('company_cases').insert(scenarioCases);

      if (caseError) {
        console.warn('デモ案件作成警告:', caseError);
      }
    }
  }

  /**
   * フォールバックデータの更新
   * デモデータリセット後に即座にデータが表示されるように調整
   */
  private refreshLocalFallbackData(): void {
    try {
      console.log('🔄 フォールバックデータを更新中...');

      // MasterDataContextで使用される可能性のあるローカルキャッシュをクリア
      const keys = ['demo_land_transporters', 'demo_cases', 'demo_business_partners'];
      keys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch {
          // LocalStorage利用不可の環境では無視
        }
      });

      console.log('✅ フォールバックデータ更新完了');
    } catch (error) {
      console.warn('フォールバックデータ更新エラー:', error);
      // エラーが発生しても処理は継続
    }
  }

  // ========================================
  // CompanyVehicleModelSettings CRUD operations
  // ========================================

  async getCompanyVehicleModelSettings(
    companyId: string
  ): Promise<CompanyVehicleModelSettings | null> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabaseが利用できません。デフォルト設定を返します。');
      return null;
    }

    try {
      const result = await this.supabase
        .from('company_vehicle_model_settings')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (!result) {
        console.error('[SupabaseService] Supabaseからの応答がありません。');
        return null;
      }

      const { data, error } = result;

      if (error && error.code !== 'PGRST116') {
        console.error('[SupabaseService] CompanyVehicleModelSettings取得エラー:', {
          error,
          companyId,
          errorCode: error.code,
          errorMessage: error.message,
        });
        return null;
      }

      if (!data) {
        return null;
      }

      return {
        id: data.id,
        companyId: data.company_id,
        enabledModelIds: data.enabled_model_ids || [],
        dataSourcePreferences: {
          allowRakuhaiProvided: data.allow_rakuhai_provided,
          allowCustom: data.allow_custom,
        },
        countryPreferences: {
          allowDomestic: data.allow_domestic,
          allowForeign: data.allow_foreign,
        },
        powertrainPreferences: {
          allowGasoline: data.allow_gasoline,
          allowDiesel: data.allow_diesel,
          allowHybrid: data.allow_hybrid,
          allowElectric: data.allow_electric,
          allowOther: data.allow_other_powertrain,
        },
        tagPreferences: {
          allowImmobilized: data.allow_immobilized,
          allowLowHeight: data.allow_low_height,
          allowWideBody: data.allow_wide_body,
          allowWelfare: data.allow_welfare,
          allowCamping: data.allow_camping,
          allowClassic: data.allow_classic,
        },
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error) {
      console.error('CompanyVehicleModelSettings取得エラー:', error);
      return null;
    }
  }

  async createCompanyVehicleModelSettings(
    settings: Omit<CompanyVehicleModelSettings, 'id'>
  ): Promise<CompanyVehicleModelSettings> {
    if (!isSupabaseAvailable() || !this.supabase) {
      const mockSettings: CompanyVehicleModelSettings = {
        id: 'mock-' + Date.now(),
        ...settings,
      };
      console.warn('Supabaseが利用できないため、CompanyVehicleModelSettings作成をスキップします');
      return mockSettings;
    }

    try {
      const { data, error } = await this.supabase
        .from('company_vehicle_model_settings')
        .upsert(
          {
            company_id: settings.companyId,
            enabled_model_ids: settings.enabledModelIds,
            allow_rakuhai_provided: settings.dataSourcePreferences.allowRakuhaiProvided,
            allow_custom: settings.dataSourcePreferences.allowCustom,
            allow_domestic: settings.countryPreferences.allowDomestic,
            allow_foreign: settings.countryPreferences.allowForeign,
            allow_gasoline: settings.powertrainPreferences.allowGasoline,
            allow_diesel: settings.powertrainPreferences.allowDiesel,
            allow_hybrid: settings.powertrainPreferences.allowHybrid,
            allow_electric: settings.powertrainPreferences.allowElectric,
            allow_other_powertrain: settings.powertrainPreferences.allowOther,
            allow_immobilized: settings.tagPreferences.allowImmobilized,
            allow_low_height: settings.tagPreferences.allowLowHeight,
            allow_wide_body: settings.tagPreferences.allowWideBody,
            allow_welfare: settings.tagPreferences.allowWelfare,
            allow_camping: settings.tagPreferences.allowCamping,
            allow_classic: settings.tagPreferences.allowClassic,
          },
          {
            onConflict: 'company_id',
          }
        )
        .select()
        .single();

      if (error) {
        console.error('[SupabaseService] CompanyVehicleModelSettings作成エラー:', {
          error,
          settings,
          errorCode: error.code,
          errorMessage: error.message,
          supabaseInitialized: !!this.supabase,
        });
        throw error;
      }

      // データベースからの戻り値をCompanyVehicleModelSettings型に変換
      return {
        id: data.id,
        companyId: data.company_id,
        enabledModelIds: data.enabled_model_ids || [],
        dataSourcePreferences: {
          allowRakuhaiProvided: data.allow_rakuhai_provided,
          allowCustom: data.allow_custom,
        },
        countryPreferences: {
          allowDomestic: data.allow_domestic,
          allowForeign: data.allow_foreign,
        },
        powertrainPreferences: {
          allowGasoline: data.allow_gasoline,
          allowDiesel: data.allow_diesel,
          allowHybrid: data.allow_hybrid,
          allowElectric: data.allow_electric,
          allowOther: data.allow_other_powertrain,
        },
        tagPreferences: {
          allowImmobilized: data.allow_immobilized,
          allowLowHeight: data.allow_low_height,
          allowWideBody: data.allow_wide_body,
          allowWelfare: data.allow_welfare,
          allowCamping: data.allow_camping,
          allowClassic: data.allow_classic,
        },
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error) {
      console.error('[SupabaseService] CompanyVehicleModelSettings作成エラー (catch):', {
        error,
        settings,
        supabaseInitialized: !!this.supabase,
      });
      throw error;
    }
  }

  async updateCompanyVehicleModelSettings(
    id: string,
    settings: Partial<CompanyVehicleModelSettings>
  ): Promise<CompanyVehicleModelSettings> {
    if (!isSupabaseAvailable() || !this.supabase) {
      // モック用に現在の設定を取得して更新したものを返す
      const mockSettings: CompanyVehicleModelSettings = {
        id: id,
        companyId: settings.companyId || 'mock-company',
        enabledModelIds: settings.enabledModelIds || [],
        dataSourcePreferences: settings.dataSourcePreferences || {
          allowRakuhaiProvided: true,
          allowCustom: true,
        },
        countryPreferences: settings.countryPreferences || {
          allowDomestic: true,
          allowForeign: false,
        },
        powertrainPreferences: settings.powertrainPreferences || {
          allowGasoline: true,
          allowDiesel: true,
          allowHybrid: true,
          allowElectric: true,
          allowOther: true,
        },
        tagPreferences: settings.tagPreferences || {
          allowImmobilized: true,
          allowLowHeight: true,
          allowWideBody: true,
          allowWelfare: true,
          allowCamping: true,
          allowClassic: true,
        },
        createdAt: settings.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      console.warn('Supabaseが利用できないため、CompanyVehicleModelSettings更新をスキップします');
      return mockSettings;
    }

    try {
      const updateData: any = {};

      if (settings.enabledModelIds !== undefined) {
        updateData.enabled_model_ids = settings.enabledModelIds;
      }

      if (settings.dataSourcePreferences) {
        updateData.allow_rakuhai_provided = settings.dataSourcePreferences.allowRakuhaiProvided;
        updateData.allow_custom = settings.dataSourcePreferences.allowCustom;
      }

      if (settings.countryPreferences) {
        updateData.allow_domestic = settings.countryPreferences.allowDomestic;
        updateData.allow_foreign = settings.countryPreferences.allowForeign;
      }

      if (settings.powertrainPreferences) {
        updateData.allow_gasoline = settings.powertrainPreferences.allowGasoline;
        updateData.allow_diesel = settings.powertrainPreferences.allowDiesel;
        updateData.allow_hybrid = settings.powertrainPreferences.allowHybrid;
        updateData.allow_electric = settings.powertrainPreferences.allowElectric;
        updateData.allow_other_powertrain = settings.powertrainPreferences.allowOther;
      }

      if (settings.tagPreferences) {
        updateData.allow_immobilized = settings.tagPreferences.allowImmobilized;
        updateData.allow_low_height = settings.tagPreferences.allowLowHeight;
        updateData.allow_wide_body = settings.tagPreferences.allowWideBody;
        updateData.allow_welfare = settings.tagPreferences.allowWelfare;
        updateData.allow_camping = settings.tagPreferences.allowCamping;
        updateData.allow_classic = settings.tagPreferences.allowClassic;
      }

      updateData.updated_at = new Date().toISOString();

      const { data, error } = await this.supabase
        .from('company_vehicle_model_settings')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('CompanyVehicleModelSettings更新エラー:', error);
        throw error;
      }

      // データベースからの戻り値をCompanyVehicleModelSettings型に変換
      return {
        id: data.id,
        companyId: data.company_id,
        enabledModelIds: data.enabled_model_ids || [],
        dataSourcePreferences: {
          allowRakuhaiProvided: data.allow_rakuhai_provided,
          allowCustom: data.allow_custom,
        },
        countryPreferences: {
          allowDomestic: data.allow_domestic,
          allowForeign: data.allow_foreign,
        },
        powertrainPreferences: {
          allowGasoline: data.allow_gasoline,
          allowDiesel: data.allow_diesel,
          allowHybrid: data.allow_hybrid,
          allowElectric: data.allow_electric,
          allowOther: data.allow_other_powertrain,
        },
        tagPreferences: {
          allowImmobilized: data.allow_immobilized,
          allowLowHeight: data.allow_low_height,
          allowWideBody: data.allow_wide_body,
          allowWelfare: data.allow_welfare,
          allowCamping: data.allow_camping,
          allowClassic: data.allow_classic,
        },
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error) {
      console.error('CompanyVehicleModelSettings更新エラー:', error);
      throw error;
    }
  }

  async deleteCompanyVehicleModelSettings(id: string): Promise<void> {
    if (!isSupabaseAvailable() || !this.supabase) {
      console.warn('Supabaseが利用できないため、CompanyVehicleModelSettings削除をスキップします');
      return;
    }

    try {
      const { error } = await this.supabase
        .from('company_vehicle_model_settings')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('CompanyVehicleModelSettings削除エラー:', error);
        throw error;
      }
    } catch (error) {
      console.error('CompanyVehicleModelSettings削除エラー:', error);
      throw error;
    }
  }
}
